import { amazonIncentives } from 'assets/assets'
import STRINGS from 'utils/strings'
import pkg from '../../package.json'
/* Special deployment regions */
export const HPI_DEPLOY_REGIONS = {
  China: 'China',
}

/* Determines if the app is being deployed in China */
export const isDeployRegionChina = () => window.__APP_CONFIG__?.HPI_DEPLOY_REGION === HPI_DEPLOY_REGIONS.China

export const STUDY_CONFIG_MAP = {
  caseManagement: 'case_management',
  clinro: 'clinro',
  consentRequired: 'consent_required',
  customGeofenceBehavior: 'custom_geofence_behavior',
  customMapButton: 'custom_map_button',
  customOnboarding: 'custom_onboarding',
  dataSyncUI: 'data_sync_ui_enabled',
  forcedInstrumentPriority: 'forced_instrument_priority',
  hasAdvancedFeatures: 'hasAdvancedFeatures',
  medicationAdherence: 'medication_adherence',
  showParticipantUniqueIdentifier: 'show_participant_unique_identifier',
  studyEnd: 'study_end',
  toneOfVoice: 'neutral_reward_enabled',
  training: 'training',
  treasureChestStoreGiftCard: 'super_gem_store_payouts',
  giftCardTypeList: 'gift_card_type_list',
  donations: 'donations',
  giftCards: 'gift_cards',
  superGems: 'super_gems',
  treasureChestGiftCardConfig: 'treasure_chest_gift_card_config',
  giftCardsForUIObj: 'gift_card_for_ui_obj',
  showPreview: 'show_preview',
  visitSupport: 'visit_support',
  virtualVisits: 'virtual_visits',
  irtControlledVisits: 'irt_controlled_visits',
  movePtps: 'move_participants',
  studyPromotion: 'study_promotion',
  zendesk: 'zendesk',
  multiSchedule: 'multi_schedule',
  schedulingEngine: 'scheduling_engine',
}

export const STUDY_SUBCONFIG_MAP = {
  rewardPropsEditingDisabled: 'reward_props_editing_disabled',
}

/** Default shape for study.config.irt_controlled_visits when absent or partial. */
export const DEFAULT_IRT_CONTROLLED_VISITS_CONFIG = {
  enabled: false,
  irt_visit_ids: [],
  irt_role_ids: [],
}

export const QUESTION_TYPE_MAP = {
  audioRecording: 'audio_recording',
  date: 'date',
  datetime: 'datetime',
  decimal: 'decimal',
  imageCapture: 'image_capture',
  integer: 'integer',
  integratedDevice: 'integrated_device',
  introduction: 'introduction',
  likert: 'likert',
  /** Horizontal Select One: numeric boxes with optional left/right anchors; 2–25 scale points */
  horizontalSelectOne: 'horizontal_select_one',
  longListSelectOne: 'long_list_select_one',
  matrix: 'matrix',
  multipleField: 'multiple_field',
  numericRatingScale: 'numeric_rating_scale',
  pdf: 'pdf',
  prompt: 'prompt',
  scribble: 'scribble',
  selectMultiple: 'select_multiple',
  selectOne: 'select_one',
  signature: 'signature',
  text: 'text',
  time: 'time',
  timer: 'timer',
  vaScale: 'va_scale',
  vaScale2021: 'va_scale_2021',
  vasHorizontal: 'vas_horizontal',
  foodEntry: 'food_entry',
  acq7: 'acq7_q7',
  timedMatchingGrid: 'timed_matching_grid',
}

/** Timed Matching Grid (SDMT-style) builder defaults and bounds */
export const TIMED_MATCHING_GRID_KEY_SLOT_COUNT = 9
export const TIMED_MATCHING_GRID_MIN_IMAGE_SIZE = 64
export const TIMED_MATCHING_GRID_MAX_IMAGE_SIZE = 256
export const TIMED_MATCHING_GRID_MAX_IMAGE_BYTES = 2500000
export const TIMED_MATCHING_GRID_MAX_SVG_BYTES = 256000
export const TIMED_MATCHING_GRID_TIMER_DISPLAY = {
  countdown: 'countdown',
  countup: 'countup',
}
export const TIMED_MATCHING_GRID_INPUT_TYPE = {
  digit: 'digit',
  checkmark: 'checkmark',
}
export const TIMED_MATCHING_GRID_KEY_MAPPING = {
  fixed19: 'fixed_1_9',
}
export const TIMED_MATCHING_GRID_DEFAULTS = {
  gridRows: 7,
  gridColumns: 7,
  gridMin: 1,
  gridMax: 20,
  timeLimitSeconds: 90,
  timeLimitSecondsMin: 1,
  timeLimitSecondsMax: 600,
  timerDisplay: TIMED_MATCHING_GRID_TIMER_DISPLAY.countdown,
  randomizeItems: true,
  keyMapping: TIMED_MATCHING_GRID_KEY_MAPPING.fixed19,
  inputType: TIMED_MATCHING_GRID_INPUT_TYPE.digit,
}
export const TIMED_MATCHING_GRID_TIMER_DISPLAY_OPTIONS = [
  { key: 'countdown', value: TIMED_MATCHING_GRID_TIMER_DISPLAY.countdown, text: 'Countdown' },
  { key: 'countup', value: TIMED_MATCHING_GRID_TIMER_DISPLAY.countup, text: 'Countup' },
]
export const TIMED_MATCHING_GRID_INPUT_TYPE_OPTIONS = [
  { key: 'digit', value: TIMED_MATCHING_GRID_INPUT_TYPE.digit, text: 'Digit 1–9' },
  {
    key: 'checkmark',
    value: TIMED_MATCHING_GRID_INPUT_TYPE.checkmark,
    text: 'Checkmark for incorrect answers',
  },
]
export const TIMED_MATCHING_GRID_KEY_MAPPING_OPTIONS = [
  {
    key: 'fixed_1_9',
    value: TIMED_MATCHING_GRID_KEY_MAPPING.fixed19,
    text: 'Symbol → 1–9 (fixed)',
  },
]

export const CONSENT_ITEM_EDITOR_ENABLED_QUESTION_TYPES_ARR = [
  QUESTION_TYPE_MAP.selectOne,
  QUESTION_TYPE_MAP.selectMultiple,
  QUESTION_TYPE_MAP.text,
  QUESTION_TYPE_MAP.prompt,
  QUESTION_TYPE_MAP.signature,
]

export const QUESTION_TYPES_WITHOUT_QUESTION_NUMBERS = [
  QUESTION_TYPE_MAP.introduction,
  QUESTION_TYPE_MAP.prompt,
  QUESTION_TYPE_MAP.signature,
  QUESTION_TYPE_MAP.pdf,
]

export const SURVEY_QUESTION_TYPE_OPTIONS = [
  { key: 'select_one', value: 'select_one', text: 'Select One' },
  { key: 'select_multiple', value: 'select_multiple', text: 'Select Multiple' },
  // { key: 'long_list_select_one', value: 'long_list_select_one', text: 'Select One (10+ options)' },
  { key: 'text', value: 'text', text: 'Text' },
  { key: 'image_capture', value: 'image_capture', text: 'Photo Submission' },
  { key: 'integer', value: 'integer', text: 'Number' },
  { key: 'decimal', value: 'decimal', text: 'Decimal' },
  { key: 'multiple_field', value: 'multiple_field', text: 'Multiple Field with Labels' },
  { key: 'date', value: 'date', text: 'Date' },
  { key: 'time', value: 'time', text: 'Time' },
  { key: 'datetime', value: 'datetime', text: 'Date & Time' },
  { key: 'likert', value: 'likert', text: 'Likert' },
  {
    key: 'horizontal_select_one',
    value: 'horizontal_select_one',
    text: 'Horizontal Select One',
  },
  { key: 'numeric_rating_scale', value: 'numeric_rating_scale', text: 'Numeric Rating Scale' },
  { key: 'matrix', value: 'matrix', text: 'Matrix' },
  { key: 'va_scale', value: 'va_scale', text: 'EQ VAS 2018' },
  { key: 'va_scale_2021', value: 'va_scale_2021', text: 'EQ VAS 2021' },
  { key: 'timer', value: 'timer', text: 'Timer' },
  { key: 'vas_horizontal', value: 'vas_horizontal', text: 'VAS (horizontal)' },
  { key: 'audio_recording', value: 'audio_recording', text: 'Audio Capture' },
  { key: 'scribble', value: 'scribble', text: 'Draw/Signature' },
  { key: 'food_entry', value: 'food_entry', text: 'Food Entry' },
  { key: 'integrated_device', value: 'integrated_device', text: 'Integrated Device' },
  { key: 'timed_matching_grid', value: 'timed_matching_grid', text: 'Timed Matching Grid' },
]

export const DIARY_QUESTION_TYPE_OPTIONS = [
  { key: 'select_one', value: 'select_one', text: 'Select One' },
  { key: 'select_multiple', value: 'select_multiple', text: 'Select Multiple' },
  { key: 'text', value: 'text', text: 'Text' },
  { key: 'integer', value: 'integer', text: 'Number' },
  { key: 'decimal', value: 'decimal', text: 'Decimal' },
  { key: 'multiple_field', value: 'multiple_field', text: 'Multiple Field with Labels' },
  { key: 'date', value: 'date', text: 'Date' },
  { key: 'time', value: 'time', text: 'Time' },
  { key: 'datetime', value: 'datetime', text: 'Date & Time' },
  { key: 'numeric_rating_scale', value: 'numeric_rating_scale', text: 'Numeric Rating Scale' },
]

export const CLINRO_QUESTION_TYPE_OPTIONS = [
  { key: 'select_one', value: 'select_one', text: 'Select One' },
  { key: 'select_multiple', value: 'select_multiple', text: 'Select Multiple' },
  { key: 'numeric_rating_scale', value: 'numeric_rating_scale', text: 'Numeric Rating Scale' },
  { key: 'horizontal_select_one', value: 'horizontal_select_one', text: 'Horizontal Select One' },
  { key: 'matrix', value: 'matrix', text: 'Matrix' },
  { key: 'text', value: 'text', text: 'Text' },
  { key: 'image_capture', value: 'image_capture', text: 'Photo Submission' },
  { key: 'datetime', value: 'datetime', text: 'Date & Time' },
  { key: 'scribble', value: 'scribble', text: 'Draw/Signature' },
  { key: 'integer', value: 'integer', text: 'Number' },
  { key: 'decimal', value: 'decimal', text: 'Decimal' },
  { key: 'acq7_q7', value: 'acq7_q7', text: 'ACQ-7' },
  { key: 'timed_matching_grid', value: 'timed_matching_grid', text: 'Timed Matching Grid' },
]

export const CONSENT_QUESTION_TYPE_OPTIONS = [
  { key: 'select_one', value: 'select_one', text: 'Select One' },
  { key: 'select_multiple', value: 'select_multiple', text: 'Select Multiple' },
  { key: 'text', value: 'text', text: 'Text' },
]

export const QUESTION_TYPES_WITH_SCORING = [
  QUESTION_TYPE_MAP.matrix,
  QUESTION_TYPE_MAP.numericRatingScale,
  QUESTION_TYPE_MAP.horizontalSelectOne,
  QUESTION_TYPE_MAP.selectMultiple,
  QUESTION_TYPE_MAP.selectOne,
  QUESTION_TYPE_MAP.timedMatchingGrid,
]

export const QUESTION_TYPES_WITH_OPT_OUT = [QUESTION_TYPE_MAP.numericRatingScale]

export const QUESTION_TYPES_WITH_IMAGE = [
  QUESTION_TYPE_MAP.audioRecording,
  QUESTION_TYPE_MAP.numericRatingScale,
  QUESTION_TYPE_MAP.horizontalSelectOne,
  QUESTION_TYPE_MAP.scribble,
  QUESTION_TYPE_MAP.selectOne,
  QUESTION_TYPE_MAP.selectMultiple,
  QUESTION_TYPE_MAP.text,
  QUESTION_TYPE_MAP.imageCapture,
  QUESTION_TYPE_MAP.prompt,
  QUESTION_TYPE_MAP.longListSelectOne,
  QUESTION_TYPE_MAP.foodEntry,
]

export const QUESTION_TYPES_WITH_AUDIO = [
  QUESTION_TYPE_MAP.horizontalSelectOne,
  QUESTION_TYPE_MAP.prompt,
  QUESTION_TYPE_MAP.text,
  QUESTION_TYPE_MAP.selectOne,
  QUESTION_TYPE_MAP.selectMultiple,
  QUESTION_TYPE_MAP.longListSelectOne,
]

export const QUESTION_TYPES_WITH_VIDEO = [
  QUESTION_TYPE_MAP.horizontalSelectOne,
  QUESTION_TYPE_MAP.prompt,
  QUESTION_TYPE_MAP.text,
  QUESTION_TYPE_MAP.selectOne,
  QUESTION_TYPE_MAP.selectMultiple,
  QUESTION_TYPE_MAP.longListSelectOne,
]

export const QUESTION_TYPES_WITH_AUTO_EXPANDING_ITEM_EDITOR = [
  QUESTION_TYPE_MAP.selectOne,
  QUESTION_TYPE_MAP.signature,
  QUESTION_TYPE_MAP.prompt,
]

const TASK_NAME_MAP = {
  rapidNaming: 'rapid_naming',
}

export const GEOFENCEABLE_TASKS = ['NYUChatbot', 'JanssenChatbot']

export const TASKS_WITH_AUDIO_PERMISSION = [TASK_NAME_MAP.rapidNaming]
export const TASKS_WITH_STORAGE_PERMISSION = [TASK_NAME_MAP.rapidNaming]

export const MOBILE_APP_PERMISSION_NAMES_MAP = {
  audioRecording: 'audio_recording',
  storageAccess: 'storage_access',
  integratedDevice: 'bluetooth',
}

export const NUMERIC_CHOICE_QUESTION_TYPES = [QUESTION_TYPE_MAP.likert, QUESTION_TYPE_MAP.numericRatingScale]

/** Horizontal Select One: values are configured per option; choice count is configurable. */
export const HORIZONTAL_SELECT_ONE_MIN_CHOICES = 2
export const HORIZONTAL_SELECT_ONE_MAX_CHOICES = 25

export const SELECT_QUESTION_TYPES = [QUESTION_TYPE_MAP.selectOne, QUESTION_TYPE_MAP.selectMultiple]

export const QUESTION_TYPES_WITH_CHOICE_IMAGES = [QUESTION_TYPE_MAP.selectOne, QUESTION_TYPE_MAP.selectMultiple]

export const QUESTION_TYPES_WITH_CHOICES = [
  ...NUMERIC_CHOICE_QUESTION_TYPES,
  QUESTION_TYPE_MAP.horizontalSelectOne,
  ...SELECT_QUESTION_TYPES,
  QUESTION_TYPE_MAP.longListSelectOne,
  QUESTION_TYPE_MAP.multipleField,
]

export const SCALE_QUESTION_TYPES = [
  QUESTION_TYPE_MAP.vaScale,
  QUESTION_TYPE_MAP.vaScale2021,
  QUESTION_TYPE_MAP.vasHorizontal,
]

export const QUESTION_TYPES_WITH_HINTS = [QUESTION_TYPE_MAP.vasHorizontal, QUESTION_TYPE_MAP.vaScale2021]
export const QUESTION_TYPES_WITH_CAREGIVER_HINTS = [QUESTION_TYPE_MAP.vaScale2021]

export const CONSENT_QUESTION_TYPES_WITHOUT_LOGIC = [QUESTION_TYPE_MAP.pdf, QUESTION_TYPE_MAP.signature]

export const INPUT_VALIDATION_TYPE_MAP = {
  absDate: 'absolute_date',
  absTime: 'absolute_time',
  decimalPlaces: 'decimal_places',
  max: 'max',
  min: 'min',
  relative: 'relative',
  acceptFutureValues: 'accept_future_values',
}

export const USER_ROLES_MAP = {
  root: 'Root',
  dchAdmin: 'DCH Admin',
  dchPii: 'DCH PII',
  dchStudyCoordinator: 'DCH Study Coordinator',
  studyCoordinator: 'Study Coordinator',
  investigator: 'Investigator',
  studyAdmin: 'Study Admin',
  sponsor: 'Sponsor',
  researcher: 'Researcher',
  enroller: 'Enroller',
  siteCoordinator: 'Site Coordinator',
  auditor: 'Auditor',
  caseManager: 'Case Manager',
  clinicianI: 'Clinical Staff Level 1',
  clinicianII: 'Clinical Staff Level 2',
  clinicianIII: 'Clinical Staff Level 3',
  clinicalCoordinator: 'Clinical Coordinator',
  supportAgent: 'Support Agent',
  reporter: 'Reporter',
  siteUserManagementAdmin: 'Site and User Management Admin',
  impartialWitness: 'Impartial Witness',
  siteCoordinatorUAT: 'Site Coordinator UAT',
  clinicalCoordinatorUAT: 'Clinical Coordinator UAT',
  sponsorUAT: 'Sponsor UAT',
  siteCoordinatorDEV: 'Site Coordinator DEV',
  clinicalCoordinatorDEV: 'Clinical Coordinator DEV',
  sponsorDEV: 'Sponsor DEV',
  sponsorUserMonitor: 'Sponsor User Monitor',
}

export const ROLE_NAME_ID_MAP = {
  Root: 1,
  DCH_Admin: 2,
  Study_Coordinator: 4,
  Investigator: 4,
  Sponsor: 5,
  Researcher: 6,
  Enroller: 7,
  Site_Coordinator: 8,
  Auditor: 9,
  Case_Manager: 10,
  Clinical_Staff_Level_1: 11,
  Clinical_Staff_Level_2: 12,
  Clinical_Staff_Level_3: 13,
  Clinical_Coordinator: 16,
  Support_Agent: 17,
  Reporter: 18,
  Site_User_Management_Admin: 19,
  Impartial_Witness: 20,
  Site_Coordinator_UAT: 21,
  Clinical_Coordinator_UAT: 22,
  Sponsor_UAT: 23,
  Site_Coordinator_DEV: 24,
  Clinical_Coordinator_DEV: 25,
  Sponsor_DEV: 26,
  Sponsor_User_Monitor: 27,
}

export const USER_ROLES_WITH_PII_NAME_ID_MAP = {
  root: { name: 'Root', id: 1 },
  dchPii: { name: 'DCH PII', id: 14 },
  studyCoordinator: { name: 'Study Coordinator', id: 4 },
  investigator: { name: 'Investigator', id: 4 },
  enroller: { name: 'Enroller', id: 7 },
  siteCoordinator: { name: 'Site Coordinator', id: 8 },
  caseManager: { name: 'Case Manager', id: 10 },
  clinicalCoordinator: { name: 'Clinical Coordinator', id: 16 },
  siteCoordinatorUAT: { name: 'Site Coordinator UAT', id: 21 },
  clinicalCoordinatorUAT: { name: 'Clinical Coordinator UAT', id: 22 },
  siteCoordinatorDEV: { name: 'Site Coordinator DEV', id: 24 },
  clinicalCoordinatorDEV: { name: 'Clinical Coordinator DEV', id: 25 },
}

export const SITE_ROLE_LIST = [
  USER_ROLES_MAP.enroller,
  USER_ROLES_MAP.siteCoordinator,
  USER_ROLES_MAP.caseManager,
  USER_ROLES_MAP.clinicianI,
  USER_ROLES_MAP.clinicianII,
  USER_ROLES_MAP.clinicianIII,
  USER_ROLES_MAP.clinicalCoordinator,
  USER_ROLES_MAP.siteUserManagementAdmin,
  USER_ROLES_MAP.impartialWitness,
  USER_ROLES_MAP.siteCoordinatorUAT,
  USER_ROLES_MAP.clinicalCoordinatorUAT,
  USER_ROLES_MAP.siteCoordinatorDEV,
  USER_ROLES_MAP.clinicalCoordinatorDEV,
]

export const AVAILABLE_USER_OPTIONS = [
  // { key: 'root', text: 'Root', value: 'root', role_id: 1 },
  // { key: 'dchAdmin', text: 'DCH Admin', value: 'dchAdmin', role_id: 2 },
  // { key: 'studyCoordinator', text: 'Study Coordinator / Investigator', value: 'studyCoordinator', role_id: 3 },
  // { key: 'investigator', text: 'Investigator', value: 'investigator', role_id: 4 },
  {
    key: 'studyCoordinator',
    text: isAtLeastVersion(pkg?.version, '3.78.0') ? 'Study Admin' : 'Study Coordinator / Investigator',
    value: 'studyCoordinator',
    role_id: 4,
  },
  { key: 'sponsor', text: 'Sponsor', value: 'sponsor', role_id: 5 },
  { key: 'researcher', text: 'Researcher', value: 'researcher', role_id: 6 },
  { key: 'enroller', text: 'Enroller', value: 'studyManager', role_id: 7 },
  { key: 'siteCoordinator', text: 'Site Coordinator', value: 'siteCoordinator', role_id: 8 },
  { key: 'auditor', text: 'Auditor', value: 'auditor', role_id: 9 },
  { key: 'caseManager', text: 'Case Manager', value: 'caseManager', role_id: 10 },
  { key: 'clinRoI', text: 'Clinician I', value: 'clinRoI', role_id: 11 },
  { key: 'clinRoII', text: 'Clinician II', value: 'clinRoII', role_id: 12 },
  { key: 'clinRoIII', text: 'Clinician III', value: 'clinRoIII', role_id: 13 },
  { key: 'clinicalCoordinator', text: 'Clinical Coordinator', value: 'clinicalCoordinator', role_id: 16 },
  { key: 'supportAgent', text: 'Support', value: 'supportAgent', role_id: 17 },
  { key: 'reporter', text: 'Reporter', value: 'reporter', role_id: 18 },
  {
    key: 'siteUserManagementAdmin',
    text: 'Site and User Management Admin',
    value: 'siteUserManagementAdmin',
    role_id: 19,
  },
  { key: 'impartialWitness', text: 'Impartial Witness', value: 'impartialWitness', role_id: 20 },
  { key: 'siteCoordinatorUAT', text: 'Site Coordinator UAT', value: 'siteCoordinatorUAT', role_id: 21 },
  {
    key: 'clinicalCoordinatorUAT',
    text: 'Clinical Coordinator UAT',
    value: 'clinicalCoordinatorUAT',
    role_id: 22,
  },
  { key: 'sponsorUAT', text: 'Sponsor UAT', value: 'sponsorUAT', role_id: 23 },
  { key: 'siteCoordinatorDEV', text: 'Site Coordinator DEV', value: 'siteCoordinatorDEV', role_id: 24 },
  {
    key: 'clinicalCoordinatorDEV',
    text: 'Clinical Coordinator DEV',
    value: 'clinicalCoordinatorDEV',
    role_id: 25,
  },
  { key: 'sponsorDEV', text: 'Sponsor DEV', value: 'sponsorDEV', role_id: 26 },
  { key: 'sponsorUserMonitor', text: 'Sponsor User Monitor', value: 'sponsorUserMonitor', role_id: 27 },
]

export const CLINICIAN_ROLES_LIST = [
  USER_ROLES_MAP.clinicianI,
  USER_ROLES_MAP.clinicianII,
  USER_ROLES_MAP.clinicianIII,
  USER_ROLES_MAP.clinicalCoordinator,
  USER_ROLES_MAP.impartialWitness,
  USER_ROLES_MAP.clinicalCoordinatorUAT,
  USER_ROLES_MAP.clinicalCoordinatorDEV,
]

export const USER_ROLE_ID_MAP = {
  root: 1,
  dchAdmin: 2,
  studyCoordinator: 3,
  investigator: 4,
  sponsor: 5,
  researcher: 6,
  enroller: 7,
  siteCoordinator: 8,
  auditor: 9,
  caseManager: 10,
  clinRoI: 11,
  clinRoII: 12,
  clinRoIII: 13,
  dchPii: 14,
  dchStudyCoordinator: 15,
  clinicalCoordinator: 16,
  supportAgent: 17,
  reporter: 18,
  siteUserManagementAdmin: 19,
  impartialWitness: 20,
  siteCoordinatorUAT: 21,
  clinicalCoordinatorUAT: 22,
  sponsorUAT: 23,
  siteCoordinatorDEV: 24,
  clinicalCoordinatorDEV: 25,
  sponsorDEV: 26,
  sponsorUserMonitor: 27,
}

// User page validation / error copy
export const USER_ASSIGN_TO_SITE_ERROR = 'Please assign the user to a site.'

export const SITE_USER_MANAGEMENT_ADMIN_DELETION_FORBIDDEN = [
  USER_ROLE_ID_MAP.root,
  USER_ROLE_ID_MAP.dchAdmin,
  USER_ROLE_ID_MAP.studyCoordinator,
  USER_ROLE_ID_MAP.investigator,
  USER_ROLE_ID_MAP.auditor,
  USER_ROLE_ID_MAP.dchPii,
  USER_ROLE_ID_MAP.dchStudyCoordinator,
  USER_ROLE_ID_MAP.supportAgent,
]

export const STUDY_ROLE_IDS = [
  USER_ROLE_ID_MAP.root,
  USER_ROLE_ID_MAP.dchAdmin,
  USER_ROLE_ID_MAP.studyCoordinator,
  USER_ROLE_ID_MAP.investigator,
  USER_ROLE_ID_MAP.sponsor,
  USER_ROLE_ID_MAP.researcher,
  USER_ROLE_ID_MAP.auditor,
  USER_ROLE_ID_MAP.dchPii,
  USER_ROLE_ID_MAP.dchStudyCoordinator,
  USER_ROLE_ID_MAP.reporter,
  USER_ROLE_ID_MAP.siteUserManagementAdmin,
  USER_ROLE_ID_MAP.sponsorUAT,
  USER_ROLE_ID_MAP.sponsorDEV,
  USER_ROLE_ID_MAP.sponsorUserMonitor,
]
export const SITE_ROLE_IDS = [
  USER_ROLE_ID_MAP.enroller,
  USER_ROLE_ID_MAP.siteCoordinator,
  USER_ROLE_ID_MAP.caseManager,
  USER_ROLE_ID_MAP.clinRoI,
  USER_ROLE_ID_MAP.clinRoII,
  USER_ROLE_ID_MAP.clinRoIII,
  USER_ROLE_ID_MAP.clinicalCoordinator,
  USER_ROLE_ID_MAP.supportAgent,
  USER_ROLE_ID_MAP.siteUserManagementAdmin,
  USER_ROLE_ID_MAP.impartialWitness,
  USER_ROLE_ID_MAP.siteCoordinatorUAT,
  USER_ROLE_ID_MAP.clinicalCoordinatorUAT,
  USER_ROLE_ID_MAP.siteCoordinatorDEV,
  USER_ROLE_ID_MAP.clinicalCoordinatorDEV,
]

export const ROOT_DCH_ADMIN_ROLE_ID_LIST = [USER_ROLE_ID_MAP.root, USER_ROLE_ID_MAP.dchAdmin]

export const USER_ROLE_IDS_WITH_UAT_ROLE_CREATION = [
  USER_ROLE_ID_MAP.root,
  USER_ROLE_ID_MAP.dchAdmin,
  USER_ROLE_ID_MAP.dchPii,
  USER_ROLE_ID_MAP.studyCoordinator,
  USER_ROLE_ID_MAP.investigator,
  // USER_ROLE_ID_MAP.dchStudyCoordinator,
]

export const USER_ROLE_IDS_CAN_ACCESS_UAT_STUDIES = [
  USER_ROLE_ID_MAP.root,
  USER_ROLE_ID_MAP.dchAdmin,
  USER_ROLE_ID_MAP.dchPii,
  USER_ROLE_ID_MAP.studyCoordinator,
  USER_ROLE_ID_MAP.investigator,
  USER_ROLE_ID_MAP.siteCoordinatorUAT,
  USER_ROLE_ID_MAP.clinicalCoordinatorUAT,
  USER_ROLE_ID_MAP.sponsorUAT,
]

export const USER_ROLE_IDS_CAN_ACCESS_DEV_STUDIES = [
  USER_ROLE_ID_MAP.root,
  USER_ROLE_ID_MAP.dchAdmin,
  USER_ROLE_ID_MAP.dchPii,
  USER_ROLE_ID_MAP.studyCoordinator,
  USER_ROLE_ID_MAP.investigator,
  USER_ROLE_ID_MAP.siteCoordinatorDEV,
  USER_ROLE_ID_MAP.clinicalCoordinatorDEV,
  USER_ROLE_ID_MAP.sponsorDEV,
]

export const DEV_USER_ROLE_IDS = [
  USER_ROLE_ID_MAP.siteCoordinatorDEV,
  USER_ROLE_ID_MAP.clinicalCoordinatorDEV,
  USER_ROLE_ID_MAP.sponsorDEV,
]

export const UAT_USER_ROLE_IDS = [
  USER_ROLE_ID_MAP.siteCoordinatorUAT,
  USER_ROLE_ID_MAP.clinicalCoordinatorUAT,
  USER_ROLE_ID_MAP.sponsorUAT,
]

export const USER_ROLE_IDS_CAN_SEE_STUDY_VERSIONS = [
  USER_ROLE_ID_MAP.root,
  USER_ROLE_ID_MAP.dchAdmin,
  USER_ROLE_ID_MAP.dchPii,
  USER_ROLE_ID_MAP.studyCoordinator,
  USER_ROLE_ID_MAP.investigator,
  // USER_ROLE_ID_MAP.dchStudyCoordinator,
]

export const USER_ROLES_CAN_ACCESS_HISTORY = [
  USER_ROLES_MAP.root,
  USER_ROLES_MAP.dchAdmin,
  USER_ROLES_MAP.investigator,
  USER_ROLES_MAP.studyCoordinator,
  USER_ROLES_MAP.studyAdmin,
  USER_ROLES_MAP.dchPii,
  USER_ROLES_MAP.sponsor,
  USER_ROLES_MAP.supportAgent,
  USER_ROLES_MAP.siteCoordinatorUAT,
  USER_ROLES_MAP.clinicalCoordinatorUAT,
  USER_ROLES_MAP.sponsorUAT,
  USER_ROLES_MAP.siteCoordinatorDEV,
  USER_ROLES_MAP.clinicalCoordinatorDEV,
  USER_ROLES_MAP.sponsorDEV,
]

/** Internal roles allowed to access Study Timeline: DCH Admin, Study Coordinator/Investigator, Root, DCH PII */
export const USER_ROLES_CAN_ACCESS_STUDY_TIMELINE = [
  USER_ROLES_MAP.root,
  USER_ROLES_MAP.dchAdmin,
  USER_ROLES_MAP.studyAdmin,
  USER_ROLES_MAP.studyCoordinator,
  USER_ROLES_MAP.dchPii,
]

export const IMPARTIAL_WITNESS_ALLOWED_CREATORS_LIST = [
  USER_ROLE_ID_MAP.root,
  USER_ROLE_ID_MAP.dchAdmin,
  USER_ROLE_ID_MAP.studyCoordinator,
  USER_ROLE_ID_MAP.investigator,
  USER_ROLE_ID_MAP.dchPii,
  USER_ROLE_ID_MAP.dchStudyCoordinator,
]

export const CLINICIAN_ROLES_ID_LIST = [
  USER_ROLE_ID_MAP.clinRoI,
  USER_ROLE_ID_MAP.clinRoII,
  USER_ROLE_ID_MAP.clinRoIII,
  USER_ROLE_ID_MAP.clinicalCoordinator,
  USER_ROLE_ID_MAP.impartialWitness,
  USER_ROLE_ID_MAP.clinicalCoordinatorUAT,
  USER_ROLE_ID_MAP.clinicalCoordinatorDEV,
]

/** ClinRO default when Study Promotion is not enabled: all 6 roles (3 clinician levels + 3 clinical coordinator). */
export const CLINRO_NON_SP_ROLE_IDS = [
  USER_ROLE_ID_MAP.clinRoI,
  USER_ROLE_ID_MAP.clinRoII,
  USER_ROLE_ID_MAP.clinRoIII,
  USER_ROLE_ID_MAP.clinicalCoordinatorDEV,
  USER_ROLE_ID_MAP.clinicalCoordinatorUAT,
  USER_ROLE_ID_MAP.clinicalCoordinator,
]

export const HIDE_PERMISSION_TABLE_ROLES_LIST = []

export const USER_ROLE_IDS_WITH_LIMITED_WRITE_USER_PERMISSIONS = [
  USER_ROLE_ID_MAP.siteCoordinator,
  USER_ROLE_ID_MAP.caseManager,
  USER_ROLE_ID_MAP.clinicalCoordinator,
  USER_ROLE_ID_MAP.siteUserManagementAdmin,
  USER_ROLE_ID_MAP.siteCoordinatorUAT,
  USER_ROLE_ID_MAP.clinicalCoordinatorUAT,
  USER_ROLE_ID_MAP.siteCoordinatorDEV,
  USER_ROLE_ID_MAP.clinicalCoordinatorDEV,
]

export const USER_ROLE_IDS_THAT_CAN_BE_CREATED_BY_SITE_ADMIN = [
  USER_ROLE_ID_MAP.enroller,
  USER_ROLE_ID_MAP.siteCoordinator,
  USER_ROLE_ID_MAP.caseManager,
  USER_ROLE_ID_MAP.clinRoI,
  USER_ROLE_ID_MAP.clinRoII,
  USER_ROLE_ID_MAP.clinRoIII,
  USER_ROLE_ID_MAP.reporter,
  USER_ROLE_ID_MAP.siteCoordinatorUAT,
  USER_ROLE_ID_MAP.siteCoordinatorDEV,
  USER_ROLE_ID_MAP.supportAgent,
]

export const USER_ROLE_IDS_CAN_EDIT_CONSENT_SITES = [
  USER_ROLE_ID_MAP.root,
  USER_ROLE_ID_MAP.dchAdmin,
  USER_ROLE_ID_MAP.studyCoordinator,
  USER_ROLE_ID_MAP.investigator,
  USER_ROLE_ID_MAP.sponsor,
  USER_ROLE_ID_MAP.researcher,
  USER_ROLE_ID_MAP.auditor,
  USER_ROLE_ID_MAP.siteUserManagementAdmin,
  USER_ROLE_ID_MAP.sponsorUAT,
  USER_ROLE_ID_MAP.sponsorDEV,
  USER_ROLE_ID_MAP.sponsorUserMonitor,
]

export const USER_ROLE_IDS_CAN_SEE_FLAGSMITH_CONFIGURATION = [
  USER_ROLE_ID_MAP.root,
  USER_ROLE_ID_MAP.dchAdmin,
  USER_ROLE_ID_MAP.dchStudyCoordinator,
]

export const USER_ROLE_IDS_CAN_EDIT_STUDY_VERSION_DETAILS = [
  USER_ROLE_ID_MAP.root,
  USER_ROLE_ID_MAP.dchAdmin,
  USER_ROLE_ID_MAP.studyCoordinator,
  USER_ROLE_ID_MAP.investigator,
  USER_ROLE_ID_MAP.dchPii,
]

export const USER_ROLE_IDS_CAN_DOWNLOAD_USER_SITE_LIST = [
  USER_ROLE_ID_MAP.root,
  USER_ROLE_ID_MAP.dchAdmin,
  USER_ROLE_ID_MAP.dchPii,
  USER_ROLE_ID_MAP.sponsorUserMonitor,
]

export const USER_ROLES_WITH_SCHEDULE_VIRTUAL_VISIT_PERMISSION = [
  USER_ROLES_MAP.root,
  USER_ROLES_MAP.studyCoordinator,
  USER_ROLES_MAP.investigator,
  USER_ROLES_MAP.studyAdmin,
  USER_ROLES_MAP.siteCoordinator,
  USER_ROLES_MAP.caseManager,
  USER_ROLES_MAP.clinicalCoordinator,
  USER_ROLES_MAP.siteCoordinatorUAT,
  USER_ROLES_MAP.clinicalCoordinatorUAT,
  USER_ROLES_MAP.siteCoordinatorDEV,
  USER_ROLES_MAP.clinicalCoordinatorDEV,
]

export const USER_ROLES_WITH_SCHEDULE_ADHOC_VISIT_PERMISSION = [
  USER_ROLES_MAP.siteCoordinator,
  USER_ROLES_MAP.clinicalCoordinator,
  USER_ROLES_MAP.siteCoordinatorUAT,
  USER_ROLES_MAP.clinicalCoordinatorUAT,
  USER_ROLES_MAP.siteCoordinatorDEV,
  USER_ROLES_MAP.clinicalCoordinatorDEV,
]

/**
 * Site and User Management Admins and Reporters are commonly restricted in many pages
 */
export const USER_ROLES_WITH_RESTRICTED_ACCESS = [USER_ROLES_MAP.siteUserManagementAdmin, USER_ROLES_MAP.reporter]
export const USER_ROLE_IDS_WITH_RESTRICTED_ACCESS = [
  USER_ROLE_ID_MAP.siteUserManagementAdmin,
  USER_ROLE_ID_MAP.reporter,
]

// Sites restrictions
export const USER_ROLES_WITH_BLOCKED_SITES_ACCESS = [USER_ROLES_MAP.reporter]
export const USER_ROLE_IDS_WITH_BLOCKED_SITES_ACCESS = [USER_ROLE_ID_MAP.reporter]

// Virtual visit restrictions
export const USER_ROLES_WITH_BLOCKED_VIRTUAL_VISIT_ACCESS = [
  USER_ROLES_MAP.enroller,
  ...USER_ROLES_WITH_RESTRICTED_ACCESS,
]
export const USER_ROLE_IDS_WITH_BLOCKED_VIRTUAL_VISIT_ACCESS = [
  USER_ROLE_ID_MAP.enroller,
  USER_ROLE_ID_MAP.supportAgent,
  ...USER_ROLE_IDS_WITH_RESTRICTED_ACCESS,
]

export const USER_ROLE_IDS_WITH_LIMITED_SIDEBAR_ACCESS = [
  USER_ROLE_ID_MAP.siteCoordinator,
  USER_ROLE_ID_MAP.siteCoordinatorUAT,
  USER_ROLE_ID_MAP.siteCoordinatorDEV,
  USER_ROLE_ID_MAP.clinicalCoordinator,
  USER_ROLE_ID_MAP.clinicalCoordinatorUAT,
  USER_ROLE_ID_MAP.clinicalCoordinatorDEV,
]

export const USER_ROLE_IDS_CLINRO_COORDINATORS = [
  USER_ROLE_ID_MAP.clinicalCoordinator,
  USER_ROLE_ID_MAP.clinicalCoordinatorUAT,
  USER_ROLE_ID_MAP.clinicalCoordinatorDEV,
]

export const USER_PAGE_TYPE_MAP = {
  create: 'create',
  edit: 'edit',
  profile: 'profile',
}

export const QUESTION_ATTRIBUTE_MAP = {
  disableButtons: 'disable_buttons',
  disableGreeting: 'disable_greeting',
  hasOptOut: 'hasOptOut',
  optOut: 'opt_out',
  /** Horizontal Select One: show alternate rich text on Linkt Web review when question label is empty */
  hasReviewScreenText: 'has_review_screen_text',
  reviewScreenText: 'review_screen_text',
  inReview: 'in_review',
  maxPhotoLimit: 'max_photo_limit',
  minChoices: 'min_choices',
  maxChoices: 'max_choices',
  minChoicesEnabled: 'min_choices_enabled',
  maxChoicesEnabled: 'max_choices_enabled',
}

export const INSTRUMENT_TYPE_MAP = {
  article: 'ARTICLE',
  consent: 'CONSENT',
  diary: 'EDIARY',
  clinro: 'CLINRO',
  survey: 'SURVEY',
  task: 'TASK',
  announcement: 'ANNOUNCEMENT',
}

export const INSTRUMENT_TYPES_WITH_SCORING = [
  INSTRUMENT_TYPE_MAP.clinro,
  INSTRUMENT_TYPE_MAP.diary,
  INSTRUMENT_TYPE_MAP.survey,
]

export const INSTRUMENT_TYPES_WITH_TRANSLATIONS = [
  INSTRUMENT_TYPE_MAP.diary,
  INSTRUMENT_TYPE_MAP.clinro,
  INSTRUMENT_TYPE_MAP.survey,
  INSTRUMENT_TYPE_MAP.article,
  INSTRUMENT_TYPE_MAP.task,
]

/** Participant surveys and diaries: Schedule → Send to exposes platform_type (Mobile/Web). Not ClinRO. */
export const INSTRUMENT_TYPES_WITH_SCHEDULE_PLATFORM = [
  INSTRUMENT_TYPE_MAP.survey,
  INSTRUMENT_TYPE_MAP.diary,
]

/** Persisted on metadata.schedule.platform_type as a string (API / survey JSON). */
export const SCHEDULE_PLATFORM_TYPE = {
  mobile: 'mobile',
  web: 'web',
  all: 'all',
}

/** Coerce legacy `{ mobile, web }`, `target_platforms`, or missing values to `mobile` | `web` | `all`. */
export const coerceSchedulePlatformType = value => {
  if (
    value === SCHEDULE_PLATFORM_TYPE.mobile ||
    value === SCHEDULE_PLATFORM_TYPE.web ||
    value === SCHEDULE_PLATFORM_TYPE.all
  ) {
    return value
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const m = value.mobile !== false
    const w = value.web !== false
    if (m && w) return SCHEDULE_PLATFORM_TYPE.all
    if (m) return SCHEDULE_PLATFORM_TYPE.mobile
    if (w) return SCHEDULE_PLATFORM_TYPE.web
    return SCHEDULE_PLATFORM_TYPE.all
  }
  return SCHEDULE_PLATFORM_TYPE.all
}

/** True if the schedule value is a valid platform (enum string or legacy object with at least one target). */
export const isSchedulePlatformTypeValid = value => {
  if (
    value === SCHEDULE_PLATFORM_TYPE.mobile ||
    value === SCHEDULE_PLATFORM_TYPE.web ||
    value === SCHEDULE_PLATFORM_TYPE.all
  ) {
    return true
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value.mobile !== false || value.web !== false
  }
  return false
}

export const REWARD_TYPE_MAP = {
  cash: 'cash',
  gems: 'gems',
  superGems: 'super_gems',
}

export const REWARD_NAME_MAP = {
  [REWARD_TYPE_MAP.cash]: 'dollars',
  [REWARD_TYPE_MAP.gems]: 'gems',
  [REWARD_TYPE_MAP.superGems]: 'supergems',
}

export const DURATION_TYPE_MAP = {
  days: 'days',
  weeks: 'weeks',
  months: 'months',
}

export const CONSENT_TYPE_MAP = {
  consent: 'consent',
  reconsent: 're_consent',
  addendum: 'addendum',
}

export const CONSENT_TYPES = {
  [CONSENT_TYPE_MAP.consent]: 'Consent',
  [CONSENT_TYPE_MAP.reconsent]: 'Reconsent',
  [CONSENT_TYPE_MAP.addendum]: 'Addendum',
}

export const CONSENT_OPTIONS_IDS = {
  consentBuilder: 'consent-builder',
  pdfUpload: 'pdf-upload',
}

export const VISIT_TIMELINE_EVENT_MAP = {
  cancelled: 'cancelled',
  createdDate: 'created_date',
  ptpConnected: 'participant_connected',
  ptpDisconnected: 'participant_disconnected',
  virtualVisitBegins: 'virtual_visit_begins',
  virtualVisitEnds: 'virtual_visit_ends',
}
export const VISIT_INFO_TYPE_MAP = {
  type: 'type',
  length: 'length',
  instrumentsCompleted: 'instruments_completed',
  notifications: 'notifications',
}

export const VISIT_NOTY_TYPE_MAP = {
  announcement: 'Announcement',
  email: 'Email',
  notification: 'Notification',
  push_notification: 'Push notification',
}

export const CURRENCY_MAP = {
  aud: 'A$',
  AUD: 'A$',
  cad: 'C$',
  CAD: 'C$',
  eur: '€',
  EUR: '€',
  gbp: '£',
  GBP: '£',
  jpy: '¥',
  JPY: '¥',
  mxp: 'Mex$',
  MXP: 'Mex$',
  usd: '$',
  USD: '$',
}

export const INSTRUMENT_PATH_MAP = {
  createClinro: 'createclinro',
  createSurvey: 'createinstrument',
  editClinro: 'editclinro',
  editSurvey: 'editinstrument',
  scoring: 'scoring',
}

export const PAYOUT_TYPE_THUMBNAIL_MAP = {
  amazon_giftcard: amazonIncentives,
}

export const PAYMENT_DELIVERY_METHODS_MAP = {
  email: 'email',
  announcement: 'announcement',
}

export const FA_ICON_MAP = {
  audio: 'fa-solid fa-play',
  document: 'fa-regular fa-file',
  download: 'fa-solid fa-download',
  externalLink: 'fa-solid fa-arrow-up-right-from-square',
  fileDownload: 'fa-solid fa-file-arrow-down',
  plus: 'fa-solid fa-plus',
  trash: 'fa-solid fa-trash-can',
  verticalEllipsis: 'fa-solid fa-ellipsis-vertical',
  video: 'fa-solid fa-play',
  windowMin: 'fa-solid fa-window-minimize',
  windowMax: 'fa-solid fa-up-right-and-down-left-from-center',
  x: 'fa-solid fa-xmark',
}

export const FILE_MIME_TYPE_MAP = {
  '.csv': 'text/csv',
  '.jpeg': 'image/jpeg',
  '.jpg': 'image/jpeg',
  '.json': 'application/json',
  '.m4v': 'video/mp4',
  '.mov': 'video/quicktime',
  '.mp3': 'audio/mpeg',
  '.mp4': 'video/mp4',
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.svg': 'image/svg+xml',
  '.wav': 'audio/wav',
  '.webm': 'video/webm',
  '.zip': 'application/zip',
}

export const SECURITY_KEY_MAP = {
  attempt_num: 'attempt_num',
  expiration: 'expiration',
  lockout_time: 'lockout_time',
  reuse_history: 'reuse_history',
  session_timeout: 'session_timeout',
  timeout_flag: 'timeout_flag',
}

export const TRIGGER_TYPE_MAP = {
  score: 'get_instrument_scores',
  compliance: 'get_instrument_compliance',
  newActivityNotification: 'send_new_activity_notification',
  singleScore: 'get_instrument_score_by_name',
}

export const BLOB_TYPE_MAP = {
  csv: 'text/csv',
  zip: 'application/x-zip-compressed',
}

export const GROUP_TYPE_MAP = {
  age: 'age',
  all: 'all_users',
  allParticipants: 'all_participants',
  allCaregivers: 'all_caregivers',
  cohort: 'study_group',
  cohorts: 'study_groups',
  sites: 'sites',
}

export const GROUP_LABEL_MAP = {
  study_groups: 'Tracks',
  sites: 'Sites',
}

/** Labels for consent deployment schedule recipient options (ConsentRecipients.js) */
export const CONSENT_RECIPIENTS_LABELS = {
  allParticipants: 'All Participants',
  sites: 'Sites',
  tracks: 'Tracks',
}

export const DEPLOYMENT_KEYS = {
  abs: 'absolute',
  chainDeployByVisit: 'chain_deployment_by_ss_visit',
  chainDeployOffset: 'chain_deploy_offset',
  chainExpireOffset: 'chain_expire_offset',
  daysOffset: 'days_offset',
  expireType: 'expire_type',
  expireOnVisitConfirmation: 'expire_on_visit_confirmation',
  firstLogin: 'first_login',
  never: 'never',
  now: 'now',
  relativeToSendIdeal: 'relative_to_send_ideal',
  visitId: 'visit_id',
  visitIds: 'visit_ids',
  visitScheduleId: 'visit_schedule_id',
  visitScheduleIds: 'visit_schedule_ids',
  studyScheduleId: 'study_schedule_id',
  studyScheduleIds: 'study_schedule_ids',
  chainExpiryInfo: 'chain_expiry_info',
  expireOnConfirm: 'expire_on_confirm',
}

// Repeat subtitle strings for scheduler UI
export const REPEAT_SUBTITLE_TEXT = {
  doesntRepeat: "Doesn't repeat",
  repeatAfterEvery: 'Repeat after every',
  repeatImmediately: 'Repeat Immediately',
}

export const DEPLOYMENT_VALUES = {
  completionOfVisit: 'completion_of_visit',
  receiptOfArticle: 'receipt_of_article',
  expireByVisit: 'expire_by_visit',
}

export const RELATIVE_DATE_INTERVAL_MAP = { preceding: 'preceding_interval', succeeding: 'succeeding_interval' }

export const urlRegex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/gm

export const getGoogleMapsApiKey = () => window.__APP_CONFIG__?.GOOGLE_MAPS_API_KEY

export const getGoogleMapsURL = () =>
  `https://maps.googleapis.com/maps/api/js?key=${getGoogleMapsApiKey()}&libraries=geometry,drawing,places`

export const getFlagsmithAPIKey = () => window.__APP_CONFIG__?.FLAGSMITH_API_KEY

export const DEFAULT_LANG = 'en_US'

export const OPERATOR_OPTIONS = [
  { key: '<', value: '<', text: 'less than' },
  { key: '<=', value: '<=', text: 'less than or equal to' },
  { key: '==', value: '==', text: 'equal to' },
  { key: '>', value: '>', text: 'greater than' },
  { key: '>=', value: '>=', text: 'greater than or equal to' },
]

export const LOGICAL_OPERATOR_CHART = {
  '<': 'less than',
  '<=': 'less than or equal to',
  '==': 'equal to',
  '>': 'greater than',
  '>=': 'greater than or equal to',
}

export const TOOLTIP_MENU = [
  { key: 'surveyCount', text: 'Surveys', icon: 'far fa-file-alt', extra: {} },
  { key: 'taskCount', text: 'Tasks', icon: 'fas fa-gamepad', extra: {} },
  { key: 'caseManagement', text: 'Case management', icon: 'fas fa-briefcase', path: 'cases' },
  { key: 'geofencing', text: 'Geofencing', icon: 'fas fa-map-marker-alt', path: 'geofences' },
  { key: 'clinro', text: 'Clinician reported outcomes', icon: 'fas fa-clipboard-list', path: 'instruments' },
  {
    key: 'medicationAdherence',
    text: 'Medication adherence',
    icon: 'fas fa-pills',
    path: 'notifications/conditional/medication_adherence',
  },
  { key: 'superGems', text: 'Treasure chest store', icon: 'far fa-gem', path: 'payments' },
]

export const TOOLTIP_MENU_MAP = {
  caseManagement: 'caseManagement',
  geofencing: 'geofencing',
  clinro: 'clinro',
  medicationAdherence: 'medicationAdherence',
  superGems: 'superGems',
}

export const CONDITION_RESOURCE_TYPE = {
  medicationAdherence: 'medication_adherence',
}

export const CONDITION_RESOURCE_FUNCTION_TYPE = {
  getMedicationCount: 'get_medication_count',
}

export const CONDITION_ACTION_TYPE = {
  sendPushNotification: 'send_push_notification',
}

export const PARTICIPANT_DATA_FIELDS_MAP = {
  dob: 'date_of_birth',
}

export const STUDY_ROUTES = {
  communication: 'communication',
}

export const SIGNATURE_TYPE_MAP = {
  draw: 'draw',
  text: 'text',
  image: 'image',
}

export const CONSENT_STATUS = {
  readyToBeScheduled: { key: 'ready_to_be_scheduled', component: 'Ready to be scheduled' },
  inProgress: { key: 'in_progress', component: 'In Progress' },
}

export const CONSENT_SIGNEE_MAP = {
  participant: 'participant',
  caregiver: 'caregiver',
  witness: 'witness',
  clinro: 'clinro',
}

export const PARTICIPANT_CONSENT_STATUS_MAP = {
  complete: { text: STRINGS.complete, sortValue: 1 },
  awaiting_clinro: { text: STRINGS.awaitingPI, longText: STRINGS.awaitingAdditionalSignature, sortValue: 2 },
  awaiting_admin: { text: STRINGS.awaitingAdmin, sortValue: 3 },
  awaiting_witness: { text: STRINGS.awaitingIw, longText: STRINGS.awaitingAdditionalSignature, sortValue: 4 },
  opened: { text: STRINGS.opened, sortValue: 5 },
  unopened: { text: STRINGS.unopened, sortValue: 6 },
  declined: { text: STRINGS.declined, sortValue: 7 },
}

export const MODAL_CONTENT_MAP = {
  actionCannotUndone: 'This action cannot be undone. Are you sure you want to delete',
  actionInvalidate:
    'This action will invalidate some logic conditions. These conditions will be removed, please confirm.',
  addStudyParticipant:
    'Clicking confirm will add this participant to your study. Ensure all participant consent has been completed before continuing.',
  approveAll: 'Are you sure you want to approve all? This action cannot be undone.',
  autoApprove: 'Would you like to turn on auto-approve?',
  cancelChanges: 'Are you sure you want to cancel? You’ll lose any changes you’ve made.',
  cannotBeUndone: 'Are you sure you want to delete this study and its associated content? This cannot be undone.',
  cancelQuestion: 'Are you sure you want to cancel',
  closeCase: 'Are you sure you want to close this case?',
  closeOut: 'Are you sure you want to close out of this?',
  deleteItem: 'Are you sure you want to delete this item?',
  deleteUser: 'Are you sure you want to delete this user? This cannot be undone.',
  deleteTrack: groupName => `Are you sure you want to delete Track ${groupName}?`,
  deleteTrackDescriptionStart: 'This will delete the track from the ',
  wholeStudy: 'whole study',
  deleteTrainingMaterial: 'Are you sure you want to delete this training?',
  deleteTrackDescriptionEnd: groupName =>
    `All participants in the study will be removed from Track ${groupName} and any deployment based on Track ${groupName} assignment will discontinue (instrument, communication, consent, etc.).`,
  deployConsent: 'Are you sure you want to deploy this consent?',
  deployInstrument: 'Are you sure you want to deploy this instrument?',
  discardPaymentAccount: 'Are you sure you want to discard this payment account?',
  logicInvalidatedByDeletion:
    'Some logic conditions will be invalidated by this deletion and will be removed. Please confirm.',
  logicInvalidatedByMove: 'Some logic conditions will be invalidated by this move and will be removed. Please confirm.',
  logicInvalidatedByType:
    'Some logic conditions will be invalidated by this type change and will be removed. Please confirm.',
  makeChange:
    'Are you sure you want to make this change?\n\nThis change may affect logic conditions assigned to other answer choices.',
  markAllDonations: 'Are you sure you want to mark all the donations as donated? This action cannot be undone.',
  newInvitation: 'A new invitation will be emailed to this participant. Would you like to send?',
  participantNewPassword: 'Participant will be sent a new temporary password on reset. Please confirm.',
  participantNewCycle: (ptpId, currentCycleName) =>
    `You are about to mark participant ${ptpId} as completing current iteration of ${currentCycleName}. This action cannot be undone.`,
  participantNewArm: 'Are you sure you want to move this participant to a different arm?',
  pleaseConfirm: 'Please confirm',
  saveChanges: 'Would you like to save your changes?',
  shuffleQuestionOrder:
    'Are you sure you want to shuffle the question order? This action is irreversible and will remove all question logic.',
  translationsUploadPrompt:
    'Existing languages have been replaced with the translations included in the CSV file. New languages have been appended. Languages not supported by the study have been discarded.',
  undone: 'This action cannot be undone.',
  updateConsent: 'Are you sure you want to update the consent?',
  userNewPassword: 'User will be sent a new temporary password on reset. Please confirm.',
  leavingThisPage: 'Leaving this page?',
  closingThisPage: 'Closing this page will delete participant details entered on this page. ',
  downloadAllAudioFiles: 'Download all audio files',
  downloadAllAudioFilesSubtitle:
    'We will compress the audio files and send a zip file to your email address associated with DCH account.',
  downloadAllAudioFilesCaption: 'This may take a couple of minutes depending on the size of the audio files.',
  didVisitHappen: 'Did this visit happen?',
  theStudyIsNotLiveYet: 'The study is not Live yet',
  theStudyIsNotAvailableYet: 'The study is not available yet',
  revokeUser:
    'Are you sure you want to revoke user access? Selecting this option would keep the user visible on the Users tab, but would remove their user access for the given study in Admin Panel.',
  reActiveUser:
    'Are you sure you want to re-enable user access? Selecting this option would reinstate the users access to the selected study.',
}

export const MODAL_BUTTONS_MAP = {
  addExistingUser: 'Add Existing User',
  cancel: 'Cancel',
  cancelVisit: 'Cancel Visit',
  closeCase: 'Close case',
  confirm: 'Confirm',
  createDiary: 'Create Diary',
  createInstrument: 'Create Instrument',
  createSurvey: 'Create Survey',
  createParticipantInstrument: 'Create Participant Instrument',
  disable: 'Disable',
  enable: 'Enable',
  goInstrumentPage: 'Go to Instruments Page',
  markAll: 'Mark all',
  nevermind: 'Nevermind',
  next: 'Next',
  no: 'No',
  okay: 'Okay',
  ok: 'OK',
  proceed: 'Proceed',
  yes: 'Yes',
  yesUpdateConsent: 'Yes, Update Consent',
  confirmAndClose: 'Confirm and Close',
  confirmAndChange: 'Confirm and change',
  createNewVersion: 'Create New Version',
}

export const VISIT_START_HOUR = 9
export const VISIT_START_MINUTE = 0
export const VISIT_START_SECOND = 0

export const MODAL_CLASSES_MAP = {
  accountDeletion: 'account-deletion',
  accountSettings: 'account-settings',
  charityPreview: 'charity-preview',
  closeCase: 'close-case',
  confirmation: 'confirmation-modal',
  createInstrument: 'create-instrument-modal',
  deleteParticipant: 'delete-participant',
  diaryDownload: 'diary-download-form',
  donation: 'donation-form',
  existingUserPrompt: 'existing-user-prompt',
  spacious: 'spacious',
  manualConsentConfirmation: 'manual-consent-confirmation',
  none: 'display-none',
  payout: 'payout-type-modal',
  photoSubmissionUploader: 'photo-submission-uploader',
  uploadMedia: 'media-upload-modal',
  uatStudyConfirmation: 'uat-study-confirmation',
}

export const SIGNATURE_TIPS = {
  camera_tip: 'Try to fit your signature, printed name, date, and version number within the frame.',
  clinro_camera_tip:
    'Try to fit the image within the frame above and ensure the center of the photo is aligned with the crosshair.',
}


export const DATE_FORMAT_MAP = {
  main: 'DD MMM YYYY',
  mainWithTime: 'DD MMM YYYY HH:mm',
  mainWithDateTime: 'DD MMM YYYY h:mm A',
  mainWithFullTime: 'DD MMM YYYY HH:mm:ss',
  datePicker: 'YYYY-MM-DD',
  datePickerWithFullTime: 'YYYY-MM-DD HH:mm:ss',
}

export const DEFAULT_VISIT_START_HOUR = 9
export const DEFAULT_VISIT_START_MINUTE = 0

export const HOURS_IN_DAY = 24
export const DAYS_IN_WEEK = 7
export const HOURS_IN_WEEK = HOURS_IN_DAY * DAYS_IN_WEEK
export const MINUTES_IN_HOUR = 60
export const MILLISECONDS_IN_SECOND = 1000
export const SECONDS_IN_MINUTE = 60
export const SECONDS_IN_HOUR = MINUTES_IN_HOUR * SECONDS_IN_MINUTE
export const SECONDS_IN_DAY = SECONDS_IN_HOUR * HOURS_IN_DAY
export const MINUTES_IN_DAY = HOURS_IN_DAY * MINUTES_IN_HOUR

export const MAX_TIMEOUT_SECONDS = 24 * SECONDS_IN_DAY // 24 days in seconds
export const DEFAULT_INACTIVITY_TIMEOUT_SECONDS = 30 * SECONDS_IN_MINUTE // 30 minutes in seconds

export const DEFAULT_NA_STRING_SORT_VAL = ''

export const INSIGHTS_DASHBOARD_KEY_MAP = {
  consentDetail: `informed_consent_detail`,
  dataSurveillance: 'data_surveillance',
  demographics: 'demographics',
  instrumentDetail: 'instrument_detail',
  location: 'location',
  riskEngagement: 'risk_engagement',
  studySpecific: 'study_specific',
}

export const DEFAULT_INSIGHTS_DASHBOARD_CONFIG = {
  enabled: true,
  custom_buttons: [],
  study_level_role_access: {
    3: true,
    4: true,
    5: true,
    6: true,
    7: true,
    8: true,
    9: true,
    10: true,
    11: true,
    12: true,
    13: true,
    15: true,
    16: true,
    17: true,
    18: true,
    19: true,
    20: true,
    21: true,
    22: true,
    23: true,
    24: true,
    25: true,
    26: true,
  },
}

export const DEFAULT_EMAIL_CONFIG = {
  display_label: 'Email',
  type: 'email',
  metadata: {
    internal_key: 'email',
    custom_validations: '',
    is_custom: false,
    default_value: '',
    can_update: true,
  },
}

export const DEFAULT_NAME_CONFIG = [
  {
    display_label: 'First Name',
    type: 'text',
    metadata: {
      internal_key: 'fname',
      custom_validations: '',
      is_custom: false,
      default_value: '',
      can_update: true,
    },
  },
  {
    display_label: 'Last Name',
    type: 'text',
    metadata: {
      internal_key: 'lname',
      custom_validations: '',
      is_custom: false,
      default_value: '',
      can_update: true,
    },
  },
]

export const NOT_APPLICABLE_PLACEHOLDER = 'n/a'
export const NOT_APPLICABLE = 'Not Applicable'

export const USER_SCOPES = {
  ca: {
    code: 'ca',
    name: 'ClinRO - Activities',
    note: 'Allow user to view/complete instruments per Participant on ClinRO platform',
  },
  cp: {
    code: 'cp',
    name: 'ClinRO - Participants',
    note: 'Allow user to search for Participant on ClinRO platform',
  },
  ft: {
    code: 'ft',
    name: 'Interact with features',
    note: 'Modify the features of a study, e.g. modify white label custom logo',
  },
  mp: {
    code: 'mp',
    name: 'Rollover Participant',
    note: 'Allows moving a participant from one study to another Participants (see items 11-13)',
  },
  ra: {
    code: 'ra',
    name: 'Read Activities',
    note:
      'Allows user to view/download surveys, tasks, eConsent forms, articles, Clinical Instruments, geofences, sites',
    text: {
      0: 'View/download surveys, tasks, eConsent forms, articles, Clinical Instruments, geofences, sites',
    },
  },
  rc: { code: 'rc', name: 'Read Consents', note: 'Read consents' },
  rd: {
    code: 'rd',
    name: 'Read Data',
    note: 'Allows to view/download data sent by participants, including compliance dashboards',
    text: {
      0: 'View/download data sent by participants',
      1: 'including compliance dashboards',
    },
  },
  roa: {
    code: 'roa',
    name: 'Read Only Activities',
    note:
      'Allow users to only read any instrument such as: surveys, tasks, eConsent forms, articles, Clinical Instruments, geofences, sites',
    text: {
      0: 'Read any instrument such as: surveys, tasks, eConsent forms, articles, Clinical Instruments, geofences, sites',
      1: 'Consent documents can be routed to the Impartial Witness for review and signature through ClinRo',
    },
  },
  rp: {
    code: 'rp',
    name: 'Read Participants',
    note:
      'View Participant Information (list and Individual). View Cohorts. Allows user to view payments. View sites. Can view cases and virtual visits.',
  },
  rup: {
    code: 'rup',
    name: 'Read Password Reset for Users',
    note: 'Allow users to reset passwords for users',
  },
  'rp-no-pii': {
    code: 'rp-no-pii',
    name: 'Read Participants (No PII)',
    note: 'Same as rp. No PII implies that PII fields will be obfuscated (ex initials only)',
  },
  rpc: {
    code: 'rpc',
    name: 'Read Participant Communications',
    note: 'Allow users to only read announcements and SMSs',
    text: {
      0: 'Read announcements and SMS',
    },
  },
  rpe: {
    code: 'rpe',
    name: 'Resending Participant Emails',
    note: 'Allow users to resend invites and reset passwords for participant',
    text: {
      0: 'Reset participant password',
      1: 'Resend invites for participants',
    },
  },
  rppv: {
    code: 'rppv',
    name: 'Read IRL Participant Visits',
    note: 'Allow users to read participant visits',
    text: {
      0: 'Read participant visits',
    },
  },
  'rppv-no-pii': {
    code: 'rppv-no-pii',
    name: 'Read IRL Participant Visits (No PII)',
    note:
      'Allow users to read participant visits. No PII implies that PII fields will be obfuscated (ex initials only)',
    text: {
      0: 'Read participant visits with no PII',
    },
  },
  rpv: {
    code: 'rpv',
    name: 'Read IRL Visits',
    note: 'Allow users to read physical visit templates',
    text: {
      0: 'Read physical visit templates',
    },
  },

  rs: {
    code: 'rs',
    name: 'Read Study',
    note: 'View study configuration properties (data enabled, cycle durations, reward type)',
  },
  ru: {
    code: 'ru',
    name: 'Read User',
    note:
      'Ability to view information about platform users. Recording behavior being used for the last 2 years: Study coordinator can see every one. Everyone else that has permission to see users, will only see users on the same site or below. (2020-07-21)',
  },
  wa: {
    code: 'wa',
    name: 'Write Activities',
    note:
      'Allows users to create, edit and delete instruments (surveys, tasks). User with this permission can create, edit, and delete geofences.',
  },
  wp: {
    code: 'wp',
    name: 'Write Participants',
    note: 'Allows users to edit and delete participants. Edit and delete cohorts.',
    text: {
      0: 'Edit and delete participants',
      1: 'Edit and delete cohorts',
    },
  },
  wppv: {
    code: 'wppv',
    name: 'Write IRL Participant Visits',
    note: 'Allow users to create, modify and execute participant visits, includes adhoc irl visits',
    text: {
      0: 'Create, modify and execute participant visits',
      1: 'includes adhoc irl visits',
    },
  },
  wpv: {
    code: 'wpv',
    name: 'Write IRL Visits',
    note: 'Allow users to create, modify and execute physical visit templates',
    text: {
      0: 'Create, modify and execute physical visit templates',
    },
  },
  wu: {
    code: 'wu',
    name: 'Write User and Study',
    note:
      'Allows users to create new, update and delete system users. Permission to create a new study. Only study coordinators and above can create/edit/delete sites. Allows scheduling Virtual Visit. (Added on 2020-06-09)',
    text: {
      0: 'Create new, update and delete users',
      1: 'Permission to create a new study',
      2: 'Only study coordinators and above can create/edit/delete sites',
      3: 'Allows scheduling Virtual Visit',
    },
  },
  wxc: {
    code: 'wxc',
    name: 'Add Cohorts',
    note: 'Allows users to add cohorts',
  },
  wxp: {
    code: 'wxp',
    name: 'Add Participants',
    note: 'Allows users to add participants',
    text: {
      0: 'Add paticipants',
    },
  },
  xa: {
    code: 'xa',
    name: 'Deploy Activities',
    note:
      'Deploy Surveys, Tasks, eConsent, articles to participants. Upload “Survey Translation” will perform 2 tasks (upload survey, deploy/make it available), therefore changed to -xa (Added on 2020-10-08)',
  },
  xc: {
    code: 'xc',
    name: 'Write and Deploy Consents',
    note: 'Write and deploy consents',
  },
  xcu: {
    code: 'xcu',
    name: 'Upload Consent Attachments',
    note: 'Upload Consent Attachments',
  },
  xp: {
    code: 'xp',
    name: 'Interact with Participants',
    note:
      'Used for sending reset passwords, resending invites, read and send announcements, modifying notifications, download files uploaded by participants. Can edit cases and schedule virtual visits.',
    text: {
      0: 'Reset passwords',
      1: 'Resending invites',
      2: 'Read announcements',
      3: 'Send announcements',
      4: 'Modifying notifications',
      5: 'Download files uploaded by participants',
      6: 'Can edit cases and schedule virtual visits',
    },
  },
  xr: {
    code: 'xr',
    name: 'Interact with Report',
    note: 'Audit function - fetching report, creating report using the filter, using audit report',
  },
  'xr-no-pii': {
    code: 'xr-no-pii',
    name: 'Interact with Report (no PII)',
    note:
      'Audit function - fetching report, creating report using the filter, using audit report. PII of participants will be obfuscated in the audit report for these users',
  },
  xs: {
    code: 'xs',
    name: 'Interact with study (delete)',
    note: 'Delete study',
  },
  xu: {
    code: 'xu',
    name: 'Modify Configuration',
    note: 'Root level permissions for: Assigning study users, modifying system variables, etc',
  },
  wpi: {
    code: 'wpi',
    name: 'Write Insights Permissions',
    note: 'Allow users to create, modify Insights Permissions',
  },
  rpi: {
    code: 'rpi',
    name: 'Read Insights Permissions',
    note: 'Allow users to read Insights Permissions',
  },
}

export const ROLE_PERMISSIONS = {
  1: {
    name: 'ROOT/Super Admin',
    allowed: [
      'ra',
      'rd',
      'rp',
      'ru',
      'rs',
      'wa',
      'wp',
      'wxp',
      'wxc',
      'wu',
      'xa',
      'rc',
      'xc',
      'xp',
      'xu',
      'xs',
      'xr',
      'ft',
      'rpe',
      'rpc',
      'roa',
      'wpv',
      'rpv',
      'wppv',
      'rppv',
      'wpi',
      'rpi',
    ],
  },
  2: {
    name: 'DCH Admin',
    allowed: [
      'ra',
      'rd',
      'ru',
      'rs',
      'wa',
      'wxc',
      'xa',
      'rc',
      'xc',
      'xf',
      'xu',
      'xr-no-pii',
      'mp',
      'ft',
      'rpe',
      'rpc',
      'roa',
      'wpv',
      'rpv',
      'wppv',
      'rppv',
      'wpi',
      'rpi',
    ],
  },
  3: {
    name: 'Study Coordinator',
    allowed: [
      'ra',
      'rd',
      'rp',
      'ru',
      'rs',
      'wa',
      'wp',
      'wxp',
      'wxc',
      'wu',
      'xa',
      'rc',
      'xc',
      'xf',
      'xp',
      'xr-no-pii',
      'mp',
      'rpe',
      'rpc',
      'roa',
      'wpv',
      'rpv',
      'wppv',
      'rppv',
      'wpi',
      'rpi',
    ],
  },
  4: {
    name: 'Investigator',
    allowed: [
      'ra',
      'rd',
      'rp',
      'ru',
      'rs',
      'wa',
      'wp',
      'wxp',
      'wxc',
      'wu',
      'xa',
      'rc',
      'xc',
      'xf',
      'xp',
      'xr-no-pii',
      'mp',
      'rpe',
      'rpc',
      'roa',
      'wpv',
      'rpv',
      'wppv',
      'rppv',
      'wpi',
      'rpi',
    ],
  },
  5: {
    name: 'Sponsor',
    allowed: ['ra', 'rd', 'ru', 'rs', 'rc', 'xr-no-pii', 'roa', 'rppv-no-pii'],
  },
  6: {
    name: 'Researcher',
    allowed: ['ra', 'rd', 'ru', 'rs', 'rc', 'roa'],
  },
  7: {
    name: 'Enroller',
    allowed: ['wp', 'wxp', 'wxc', 'rc', 'rpe', 'rpc'],
  },
  8: {
    name: 'Site Coordinator',
    allowed: [
      'ra',
      'rd',
      'rp',
      'ru',
      'rs',
      'wp',
      'wxp',
      'wxc',
      'rc',
      'xc',
      'xf',
      'xp',
      'mp',
      'rpe',
      'rpc',
      'roa',
      'rpv',
      'wppv',
      'rppv',
    ],
  },
  9: {
    name: 'Auditor',
    allowed: ['ra', 'rd', 'rp', 'ru', 'rs', 'rc', 'xr-no-pii', 'roa'],
  },
  10: {
    name: 'Case Manager',
    allowed: ['ra', 'rd', 'rp', 'ru', 'rs', 'wxp', 'wxc', 'rc', 'xp', 'rpe', 'rpc', 'roa'],
  },
  11: {
    name: 'Clinical Staff 1',
    allowed: ['ca', 'cp', 'rd', 'roa'],
  },
  12: {
    name: 'Clinical Staff 2',
    allowed: ['ca', 'cp', 'rd', 'roa'],
  },
  13: {
    name: 'Clinical Staff 3',
    allowed: ['ca', 'cp', 'rd', 'roa'],
  },
  14: {
    name: 'DCH PII',
    allowed: ['ra', 'rd', 'rp', 'ru', 'rs', 'rc', 'xf', 'xr', 'ft', 'roa', 'wpv', 'rpv', 'wppv', 'rppv', 'wpi', 'rpi'],
  },
  15: {
    name: 'DCH Study Coordinator',
    allowed: [
      'ra',
      'rd',
      'ru',
      'rs',
      'wa',
      'wxp',
      'wxc',
      'wu',
      'xa',
      'rc',
      'xc',
      'xf',
      'xu',
      'xr-no-pii',
      'mp',
      'rpe',
      'rpc',
      'roa',
      'wpv',
      'rpv',
      'wppv',
      'rppv',
      'wpi',
      'rpi',
    ],
  },
  16: {
    name: 'Clinical Coordinator',
    allowed: [
      'ca',
      'cp',
      'ra',
      'rd',
      'rp',
      'ru',
      'rs',
      'wp',
      'wxp',
      'wxc',
      'rc',
      'xc',
      'xf',
      'xp',
      'rpe',
      'rpc',
      'roa',
      'rpv',
      'wppv',
      'rppv',
    ],
  },
  17: {
    name: 'Support Agent',
    allowed: [
      'rs',
      'ra',
      'rd',
      'rp-no-pii',
      'ru',
      'rc',
      'xr-no-pii',
      'roa',
      'rppv-no-pii',
      'rsc',
      'rpe',
      'rpc',
      'rup',
    ],
  },
  18: {
    name: 'Reporter',
    allowed: ['ra', 'rd', 'rs', 'wpi', 'rpi'],
  },
  19: {
    name: 'Site and User Management Admin',
    allowed: ['ru', 'wu', 'xr-no-pii', 'rs'],
  },
  20: {
    name: 'Impartial Witness',
    allowed: ['ca', 'cp', 'rd', 'rp', 'roa'],
  },
  21: {
    name: 'Site Coordinator UAT',
    allowed: [
      'ra',
      'rd',
      'rp',
      'ru',
      'rs',
      'wp',
      'wxp',
      'wxc',
      'rc',
      'xc',
      'xf',
      'xp',
      'mp',
      'rpe',
      'rpc',
      'roa',
      'rpv',
      'wppv',
      'rppv',
    ],
  },
  22: {
    name: 'Clinical Coordinator UAT',
    allowed: [
      'ca',
      'cp',
      'ra',
      'rd',
      'rp',
      'ru',
      'rs',
      'wp',
      'wxp',
      'wxc',
      'rc',
      'xc',
      'xf',
      'xp',
      'rpe',
      'rpc',
      'roa',
      'rpv',
      'wppv',
      'rppv',
    ],
  },
  23: {
    name: 'Sponsor UAT',
    allowed: ['ra', 'rd', 'ru', 'rs', 'rc', 'xr-no-pii', 'roa', 'rppv-no-pii'],
  },

  24: {
    name: 'Site Coordinator DEV',
    allowed: [
      'ra',
      'rd',
      'rp',
      'ru',
      'rs',
      'wp',
      'wxp',
      'wxc',
      'rc',
      'xc',
      'xf',
      'xp',
      'mp',
      'rpe',
      'rpc',
      'roa',
      'rpv',
      'wppv',
      'rppv',
    ],
  },
  25: {
    name: 'Clinical Coordinator DEV',
    allowed: [
      'ca',
      'cp',
      'ra',
      'rd',
      'rp',
      'ru',
      'rs',
      'wp',
      'wxp',
      'wxc',
      'rc',
      'xc',
      'xf',
      'xp',
      'rpe',
      'rpc',
      'roa',
      'rpv',
      'wppv',
      'rppv',
    ],
  },
  26: {
    name: 'Sponsor DEV',
    allowed: ['ra', 'rd', 'ru', 'rs', 'rc', 'xr-no-pii', 'roa', 'rppv-no-pii'],
  },
  27: {
    name: 'Sponsor User Monitor',
    allowed: ['rs', 'ra', 'wu', 'rd', 'rp-no-pii', 'ru', 'rc', 'xr-no-pii', 'roa', 'rppv-no-pii', 'rsc'],
  },
}

export const AVAILABLE_INSIGHTS_ROLE_OPTIONS = [
  { key: 5, text: 'Sponsor', value: 'sponsor', role_id: 5 },
  { key: 6, text: 'Researcher', value: 'researcher', role_id: 6 },
  { key: 7, text: 'Enroller', value: 'studyManager', role_id: 7 },
  { key: 8, text: 'Site Coordinator', value: 'siteCoordinator', role_id: 8 },
  { key: 9, text: 'Auditor', value: 'auditor', role_id: 9 },
  { key: 16, text: 'Clinical Coordinator', value: 'clinicalCoordinator', role_id: 16 },
  { key: 17, text: 'Support', value: 'supportAgent', role_id: 17 },
  { key: 18, text: 'Reporter', value: 'reporter', role_id: 18 },
  { key: 27, text: 'Sponsor User Monitor', value: 'sponsorUserMonitor', role_id: 27 },
]

export const STUDY_PROMOTION_AVAILABLE_INSIGHTS_ROLE_OPTIONS = [
  { key: 5, text: 'Sponsor', value: 'sponsor', role_id: 5 },
  { key: 6, text: 'Researcher', value: 'researcher', role_id: 6 },
  { key: 7, text: 'Enroller', value: 'studyManager', role_id: 7 },
  { key: 8, text: 'Site Coordinator', value: 'siteCoordinator', role_id: 8 },
  { key: 9, text: 'Auditor', value: 'auditor', role_id: 9 },
  { key: 16, text: 'Clinical Coordinator', value: 'clinicalCoordinator', role_id: 16 },
  { key: 17, text: 'Support', value: 'supportAgent', role_id: 17 },
  { key: 18, text: 'Reporter', value: 'reporter', role_id: 18 },
  { key: 21, text: 'Site Coordinator UAT', value: 'siteCoordinatorUAT', role_id: 21 },
  { key: 22, text: 'Clinical Coordinator UAT', value: 'clinicalCoordinatorUAT', role_id: 22 },
  { key: 23, text: 'Sponsor UAT', value: 'sponsorUAT', role_id: 23 },
  { key: 24, text: 'Site Coordinator DEV', value: 'siteCoordinatorDEV', role_id: 24 },
  { key: 25, text: 'Clinical Coordinator DEV', value: 'clinicalCoordinatorDEV', role_id: 25 },
  { key: 26, text: 'Sponsor DEV', value: 'sponsorDEV', role_id: 26 },
  { key: 27, text: 'Sponsor User Monitor', value: 'sponsorUserMonitor', role_id: 27 },
]

export const DEFAULT_INSIGHTS_STUDY_ROLE_ACCESS = {
  5: true,
  6: true,
  7: true,
  8: true,
  9: true,
  10: true,
  11: true,
  12: true,
  13: true,
  16: true,
  17: true,
  18: true,
  19: true,
  20: true,
}

export const STUDY_PROMOTION_DEFAULT_INSIGHTS_STUDY_ROLE_ACCESS = {
  21: true,
  22: true,
  23: true,
  24: true,
  25: true,
  26: true,
}

export const DEFAULT_PTP_DATA_FIELDS = {
  mandatory: [
    {
      display_label: 'First Name',
      type: 'text',
      metadata: {
        internal_key: 'fname',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Last Name',
      type: 'text',
      metadata: {
        internal_key: 'lname',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Sex',
      type: 'text',
      metadata: {
        internal_key: 'gender',
        custom_validations: '',
        is_custom: false,
        default_value: 'n',
        can_update: true,
      },
    },
    {
      display_label: 'Date of Birth',
      type: 'date',
      metadata: {
        internal_key: 'date_of_birth',
        custom_validations: '',
        is_custom: false,
        default_value: '1980-01-01',
        can_update: true,
      },
    },
    {
      display_label: 'Sites',
      type: 'number',
      metadata: {
        internal_key: 'site_id',
        ui_type: 'custom_dropdown',
        custom_validations: '',
        is_custom: false,
        default_value: null,
        can_update: true,
      },
    },
    {
      display_label: 'Locale',
      type: 'text',
      metadata: {
        internal_key: 'default_locale',
        custom_validations: '',
        is_custom: false,
        default_value: 'en_US',
        can_update: true,
      },
    },
  ],
  optional: [
    {
      display_label: 'Subject ID',
      type: 'text',
      metadata: {
        internal_key: 'subject_id',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
        validation_message: 'This ID is already taken. Please choose another',
      },
    },
    {
      display_label: 'Caregiver',
      type: 'checkbox',
      metadata: {
        internal_key: 'is_caregiver',
        custom_validations: '',
        is_custom: false,
        default_value: false,
        can_update: true,
      },
    },
    {
      display_label: 'Track',
      type: 'number',
      metadata: {
        internal_key: 'study_groups',
        custom_validations: '',
        is_custom: true,
        default_value: null,
        can_update: true,
      },
    },
    {
      display_label: 'Start Date',
      type: 'date',
      metadata: {
        internal_key: 'start_date',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Visit start date',
      type: 'number',
      metadata: {
        internal_key: 'visit_start_datetime',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
  ],
  disabled: [
    {
      display_label: 'Phone',
      type: 'tel',
      metadata: {
        internal_key: 'phone',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Email',
      type: 'email',
      metadata: {
        internal_key: 'email',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
  ],
}

export const DEFAULT_CAREGIVERS_DATA_FIELDS = {
  mandatory: [
    {
      display_label: 'Caregiver',
      type: 'checkbox',
      metadata: {
        internal_key: 'is_caregiver',
        custom_validations: '',
        is_custom: false,
        default_value: false,
        can_update: false,
      },
    },
    {
      display_label: 'Locale',
      type: 'text',
      metadata: {
        internal_key: 'default_locale',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Subject ID',
      type: 'text',
      metadata: {
        internal_key: 'subject_id',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Sites',
      type: 'number',
      metadata: {
        internal_key: 'site_id',
        ui_type: 'custom_dropdown',
        custom_validations: '',
        is_custom: false,
        default_value: null,
        can_update: true,
      },
    },
  ],
  optional: [],
  disabled: [
    {
      display_label: 'First Name',
      type: 'text',
      metadata: {
        internal_key: 'fname',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Last Name',
      type: 'text',
      metadata: {
        internal_key: 'lname',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Email',
      type: 'email',
      metadata: {
        internal_key: 'email',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Phone',
      type: 'tel',
      metadata: {
        internal_key: 'phone',
        custom_validations: '',
        is_custom: false,
        default_value: '',
        can_update: true,
      },
    },
    {
      display_label: 'Sex',
      type: 'text',
      metadata: {
        internal_key: 'gender',
        custom_validations: '',
        is_custom: false,
        default_value: 'n',
        can_update: true,
      },
    },
    {
      display_label: 'Date of Birth',
      type: 'date',
      metadata: {
        internal_key: 'date_of_birth',
        custom_validations: '',
        is_custom: false,
        default_value: '1980-01-01',
        can_update: true,
      },
    },
  ],
}

export const DEFAULT_STUDY_CONFIG = {
  custom_onboarding: {},
  case_management: {},
  custom_geofence_behavior: {},
  custom_map_button: {},
  data_sync_ui_enabled: true,
  ediary: { version: 2 },
  caregivers: { version: 1 },
  modules: {
    // 3.17
    geofencing: false,
    battery: false,
    location: false,
    stepcounter: false,
    appsinstalled: false,
    screentime: false,
    phonecontacts: false,
    calls: false,
    sms: false,
    bluetooth: false,
    gmail: false,
    googlecontacts: false,
    gmailaccounts: false,
    linkedin: false,
    twitter: false,
  },
  forced_instrument_priority: false, // 3.17
  reward_duration: null, // 3.17
  reward_goal: null, // 3.17
  has_dynamic_cycles: false, // 3.17
  cycles: [
    // 3.17
    {
      target: 2,
      reward: 100,
      cycleDuration: 1,
      studyDuration: 1,
      durationType: 'days',
      rewardType: 'gems',
    },
  ],
  auto_notification: {
    // 3.17
    incomplete_instrument: {
      enabled: false,
      interval_hours: 24,
    },
    expiring_instrument: {
      enabled: false,
      interval_hours: 24,
    },
    cycle_end: {
      enabled: false,
      interval_hours: 24,
    },
    periodic: {
      enabled: false,
      interval_days: 1,
      time_of_day: '18:00',
    },
  },
  participant_data_fields: DEFAULT_PTP_DATA_FIELDS,
  caregiver_data_fields: DEFAULT_CAREGIVERS_DATA_FIELDS,
  consent_required: false,
  consent_2: {},
  require_missed_visit_reason: true,
  hasAdvancedFeatures: false,
  session_timeout_seconds: 1800,
  neutral_reward_enabled: false,
  instrument_architecture_version: 2,
  allow_auto_recovery_flows: true,
  study_validations: {
    participant_subject_id_uniqueness: { enabled: true, site_based: false },
  },
  temporary_password_valid_days: 1,
  subject_as_username: { enabled: false, prefix: 'XYZ' },
  participant_offline_duration_days: 14,
  show_participant_unique_identifier: {
    type: 'subject_id?subject_id:participant_id',
  },
  manual_consent: true,
  zendesk: {},
  is_rewardless_study: false,
  is_avatarless_study: false,
  hide_treasure_pill: false,
  incentives: { enabled: true },
  tracks: { disabled: false },
  monthly_repeat_enabled: {},
  visit_support: {
    version: 2,
    arms_enabled: true,
    disable_adhoc_visit: false,
    disable_subsequent_visit_adjustment: false,
    collect_confirmed_time: true,
    enable_visit_time: true,
  },
  virtual_visits: {
    disable_recording: true,
    disable_adhoc: true,
  },
  irt_controlled_visits: { ...DEFAULT_IRT_CONTROLLED_VISITS_CONFIG },
  track_deploy_time: false,
  food_diary: {},
  multi_schedule: {},
  unification: {},
  admin_security: {
    attempt_num: { isToggled: true, value: 6, showError: false },
    lockout_time: { isToggled: true, value: 30, showError: false },
    expiration: { isToggled: true, value: 90, showError: false },
    reuse_history: { isToggled: true, value: 6, showError: false },
    timeout_flag: 30,
  },
  training: { enabled: false },
}

/** ClinRO defaults for new studies — only Root/DCH Admin may enable this in the admin UI. */
export const DEFAULT_CLINRO_STUDY_CONFIG = { deploy_early: true, greeting: true }

/**
 * Base default study config for the create-study flow, with ClinRO only when the creator is Root or DCH Admin.
 */
export const getDefaultStudyConfigForNewStudy = isRootOrDchAdmin => {
  const config = { ...DEFAULT_STUDY_CONFIG }
  if (isRootOrDchAdmin) {
    config.clinro = DEFAULT_CLINRO_STUDY_CONFIG
  }
  return config
}

export const DEFAULT_STUDY = {
  /* Duplicate keys that are in study root level and config level are commented with the app versions that use them:
    v3.16 for app version 3.16 and below
    v3.17 for app version 3.17 and above */
  name: '',
  platform_name: '',
  modules: {
    // 3.16
    geofencing: false,
    battery: false,
    location: false,
    stepcounter: false,
    appsinstalled: false,
    screentime: false,
    phonecontacts: false,
    calls: false,
    sms: false,
    bluetooth: false,
    gmail: false,
    googlecontacts: false,
    gmailaccounts: false,
    linkedin: false,
    twitter: false,
  },
  forced_instrument_priority: false, // 3.16
  reward_duration: null, // 3.16
  reward_goal: null, // 3.16
  has_dynamic_cycles: false, // 3.16
  cycles: [
    {
      target: 2,
      reward: 100,
      cycleDuration: 1,
      studyDuration: 1,
      durationType: 'days',
      rewardType: 'gems',
    },
  ], // 3.16
  auto_notification: {
    // 3.16
    incomplete_instrument: {
      enabled: false,
      interval_hours: 24,
    },
    expiring_instrument: {
      enabled: false,
      interval_hours: 24,
    },
    cycle_end: {
      enabled: false,
      interval_hours: 24,
    },
    periodic: {
      enabled: false,
      interval_days: 1,
      time_of_day: '18:00',
    },
  },
  config: {
    custom_onboarding: {},
    case_management: {},
    custom_geofence_behavior: {},
    custom_map_button: {},
    data_sync_ui_enabled: true,
    ediary: { version: 2 },
    modules: {
      // 3.17
      geofencing: false,
      battery: false,
      location: false,
      stepcounter: false,
      appsinstalled: false,
      screentime: false,
      phonecontacts: false,
      calls: false,
      sms: false,
      bluetooth: false,
      gmail: false,
      googlecontacts: false,
      gmailaccounts: false,
      linkedin: false,
      twitter: false,
    },
    forced_instrument_priority: false, // 3.17
    reward_duration: null, // 3.17
    reward_goal: null, // 3.17
    has_dynamic_cycles: false, // 3.17
    cycles: [
      // 3.17
      {
        target: 2,
        reward: 100,
        cycleDuration: 1,
        studyDuration: 1,
        durationType: 'days',
        rewardType: 'gems',
      },
    ],
    auto_notification: {
      // 3.17
      incomplete_instrument: {
        enabled: false,
        interval_hours: 24,
      },
      expiring_instrument: {
        enabled: false,
        interval_hours: 24,
      },
      cycle_end: {
        enabled: false,
        interval_hours: 24,
      },
      periodic: {
        enabled: false,
        interval_days: 1,
        time_of_day: '18:00',
      },
    },
    participant_data_fields: JSON.parse(JSON.stringify(DEFAULT_PTP_DATA_FIELDS)),
    consent_required: false,
    require_missed_visit_reason: true,
    hasAdvancedFeatures: false,
  },
}

export const MAX_INT = 2147483647
export const MIN_INT = -2147483648

export const DECIMAL_OPTIONS = [
  { key: '', text: '', value: '' },
  { key: '1', text: '0', value: '1' },
  { key: '2', text: '00', value: '2' },
  { key: '3', text: '000', value: '3' },
  { key: '4', text: '0000', value: '4' },
  { key: '5', text: '00000', value: '5' },
  { key: '6', text: '000000', value: '6' },
  { key: '7', text: '0000000', value: '7' },
  { key: '8', text: '00000000', value: '8' },
  { key: '9', text: '000000000', value: '9' },
  { key: '10', text: '0000000000', value: '10' },
  { key: '11', text: '00000000000', value: '11' },
  { key: '12', text: '000000000000', value: '12' },
]

export const INSIGHTS_DASHBOARD_NAMES = {
  location: 'location',
  auditReport: 'audit_report',
  dataSurveillance: 'data_surveillance',
  demographics: 'demographics',
  instrumentDetail: 'instrument_detail',
}

export const TRAINING_MATERIAL_TYPE = {
  document: 'document',
  video: 'video',
}

export const REDIRECT_DELAY = 400

/** Max size for survey question video uploads (must match control_server /survey_resource). */
export const SURVEY_QUESTION_VIDEO_MAX_BYTES = 25 * 1024 * 1024
export const SURVEY_QUESTION_VIDEO_MAX_SIZE_ERROR = 'Video must be 25 MB or smaller.'

export const MEDIA_TYPE_MAP = {
  image: 'image',
  audio: 'audio',
  video: 'video',
  document: 'document',
  white_labeling: 'white_labeling',
  pdf: 'pdf',
}

export const MEDIA_UPLOAD_MAP = {
  audio: {
    acceptedTypes: ['.mp3', '.wav'],
  },
  white_labeling: {
    acceptedTypes: ['.png', '.jpg', '.jpeg'],
    restriction: 'Max size 2.5 MB, 1000 x 1000 pixels',
  },
  image: {
    acceptedTypes: ['.png', '.jpg', '.jpeg'],
    restriction: 'Max size 2.5 MB, 1000 x 1000 pixels',
  },
  general_image: {
    acceptedTypes: ['.png', '.jpg', '.jpeg'],
    restriction: 'Max 400 KB',
  },
  video: {
    acceptedTypes: ['.mp4', '.m4v', '.mov', '.webm'],
  },
  document: {
    acceptedTypes: ['.pdf'],
  },
}

export const BULK_DATA_TYPES = {
  audioFiles: 'audio_files',
}

export const STATUS_OPTIONS = {
  Confirmed: 'Confirmed',
  Missed: 'Missed',
  Passed: 'Passed',
  Scheduled: 'Scheduled',
}

export const CONSENT_STATUSES = {
  inProgress: 'in_progress',
  readyToBeScheduled: 'ready_to_be_scheduled',
  scheduled: 'scheduled',
}

export const TIMER_DEFAULT_DATE = '2000-01-01T00:00:00.000-00:00'

export const TIMER_TIME_FORMAT = 'mm:ss'

export const TIMER_TIME_OPTIONS = {
  minute: 'minute',
  second: 'second',
}

export const MIN_QUESTIONS_FOR_SHUFFLE = 2

export const INSTRUMENT_TIMELINE_EVENTS = {
  completed: 'instrument_completed',
  completedArticle: 'instrument_completed_article',
  completedDiary: 'instrument_completed_ediary',
  completedSurvey: 'instrument_completed_survey',
  completedTask: 'instrument_completed_task',
  delivered: 'instrument_delivered',
  deliveredArticle: 'instrument_delivered_article',
  deliveredDiary: 'instrument_delivered_ediary',
  deliveredSurvey: 'instrument_delivered_survey',
  deliveredTask: 'instrument_delivered_task',
  read: 'instrument_read',
  readArticle: 'instrument_read_article',
  readDiary: 'instrument_read_ediary',
  readSurvey: 'instrument_read_survey',
  readTask: 'instrument_read_task',
  seen: 'instrument_seen',
  seenArticle: 'instrument_seen_article',
  seenDiary: 'instrument_seen_ediary',
  seenSurvey: 'instrument_seen_survey',
  seenTask: 'instrument_seen_task',
  submitSuccessful: 'instrument_submit_successful',
  submitSuccessfulArticle: 'instrument_submit_successful_article',
  submitSuccessfulDiary: 'instrument_submit_successful_ediary',
  submitSuccessfulSurvey: 'instrument_submit_successful_survey',
  submitSuccessfulTask: 'instrument_submit_successful_task',
  visible: 'instrument_visible',
  visibleArticle: 'instrument_visible_article',
  visibleDiary: 'instrument_visible_ediary',
  visibleSurvey: 'instrument_visible_survey',
  visibleTask: 'instrument_visible_task',
  instrumentSendActual: 'instrument_send_actual',
}

export const ANNOUNCEMENT_TIMELINE_EVENTS = {
  delivered: 'announcement_delivered',
  visible: 'announcement_visible',
  seen: 'announcement_seen',
  read: 'announcement_read',
}

export const COMBINED_INSTRUMENT_TIMELINE_MAP = {
  [INSTRUMENT_TIMELINE_EVENTS.completed]: STRINGS.completedSentToSystem,
  [INSTRUMENT_TIMELINE_EVENTS.completedArticle]: STRINGS.completedSentToSystem,
  [INSTRUMENT_TIMELINE_EVENTS.completedDiary]: STRINGS.completedSentToSystem,
  [INSTRUMENT_TIMELINE_EVENTS.completedSurvey]: STRINGS.completedSentToSystem,
  [INSTRUMENT_TIMELINE_EVENTS.completedTask]: STRINGS.completedSentToSystem,

  [INSTRUMENT_TIMELINE_EVENTS.delivered]: STRINGS.receivedByDevice,
  [INSTRUMENT_TIMELINE_EVENTS.deliveredArticle]: STRINGS.receivedByDevice,
  [INSTRUMENT_TIMELINE_EVENTS.deliveredDiary]: STRINGS.receivedByDevice,
  [INSTRUMENT_TIMELINE_EVENTS.deliveredSurvey]: STRINGS.receivedByDevice,
  [INSTRUMENT_TIMELINE_EVENTS.deliveredTask]: STRINGS.receivedByDevice,

  [INSTRUMENT_TIMELINE_EVENTS.read]: STRINGS.opened,
  [INSTRUMENT_TIMELINE_EVENTS.readArticle]: STRINGS.opened,
  [INSTRUMENT_TIMELINE_EVENTS.readDiary]: STRINGS.opened,
  [INSTRUMENT_TIMELINE_EVENTS.readSurvey]: STRINGS.opened,
  [INSTRUMENT_TIMELINE_EVENTS.readTask]: STRINGS.opened,

  [INSTRUMENT_TIMELINE_EVENTS.seen]: STRINGS.seen,
  [INSTRUMENT_TIMELINE_EVENTS.seenArticle]: STRINGS.seen,
  [INSTRUMENT_TIMELINE_EVENTS.seenDiary]: STRINGS.seen,
  [INSTRUMENT_TIMELINE_EVENTS.seenSurvey]: STRINGS.seen,
  [INSTRUMENT_TIMELINE_EVENTS.seenTask]: STRINGS.seen,

  [INSTRUMENT_TIMELINE_EVENTS.submitSuccessful]: STRINGS.systemReceived,
  [INSTRUMENT_TIMELINE_EVENTS.submitSuccessfulArticle]: STRINGS.systemReceived,
  [INSTRUMENT_TIMELINE_EVENTS.submitSuccessfulDiary]: STRINGS.systemReceived,
  [INSTRUMENT_TIMELINE_EVENTS.submitSuccessfulSurvey]: STRINGS.systemReceived,
  [INSTRUMENT_TIMELINE_EVENTS.submitSuccessfulTask]: STRINGS.systemReceived,

  [INSTRUMENT_TIMELINE_EVENTS.visible]: STRINGS.visible,
  [INSTRUMENT_TIMELINE_EVENTS.visibleArticle]: STRINGS.visible,
  [INSTRUMENT_TIMELINE_EVENTS.visibleDiary]: STRINGS.visible,
  [INSTRUMENT_TIMELINE_EVENTS.visibleSurvey]: STRINGS.visible,
  [INSTRUMENT_TIMELINE_EVENTS.visibleTask]: STRINGS.visible,

  [INSTRUMENT_TIMELINE_EVENTS.instrumentSendActual]: STRINGS.sentToDevice,

  [ANNOUNCEMENT_TIMELINE_EVENTS.delivered]: STRINGS.receivedByDevice,
  [ANNOUNCEMENT_TIMELINE_EVENTS.visible]: STRINGS.visible,
  [ANNOUNCEMENT_TIMELINE_EVENTS.seen]: STRINGS.seen,
  [ANNOUNCEMENT_TIMELINE_EVENTS.read]: STRINGS.openedAndRead,
}

export const FLAGSMITH_FEATURES = {
  advancedEvents: 'advanced_events_timeline',
  architectureVersion: 'default_instrument_architecture_version',
  instrumentUnification: 'unification',
  foodDiary: 'food_diary_enabled',
  studyPromotion: 'study_promotion_enabled',
  maintenanceTool: 'maintenance_tool',
}

export const INSTRUMENT_ERROR_LIST = {
  instrumentScore: 'cohort.filter.instrument_score',
  instrumentScoreId: 'schedule.chain_deployment_info.cohort.filter.score_id',
}

export const INSTRUMENT_DISPLAY_LOCATION_OPTIONS = {
  carousel: 'CAROUSEL',
  calendar: 'CALENDAR',
}

export const MIN_SESSION_TIMEOUT_MINUTES = 3

export const MAX_OFFLINE_DURATION_DAYS = 14

export const STUDY_CONFIG_FEATURE_KEY_MAP = {
  consent: 'eConsent',
  offlineDuration: 'participant_offline_duration_days',
  tracks: 'tracks',
}

export const INPUT_DEBOUNCE = 1000

export const MAX_STUDIES_ON_PAGE = 12

export const STUDY_FEATURE_MAP = {
  caseManagement: 'caseManagement',
  geofencing: 'geofencing',
  clinro: 'clinro',
  medicationAdherence: 'medicationAdherence',
  superGems: 'superGems',
  testStudy: 'testStudy',
  emailless: 'emailless',
  econsent: 'econsent',
}

export const STUDY_FEATURE_FILTER_OPTIONS = [
  { key: STUDY_FEATURE_MAP.caseManagement, value: STUDY_FEATURE_MAP.caseManagement, text: 'Case management' },
  { key: STUDY_FEATURE_MAP.geofencing, value: STUDY_FEATURE_MAP.geofencing, text: 'Geofencing' },
  { key: STUDY_FEATURE_MAP.clinro, value: STUDY_FEATURE_MAP.clinro, text: 'Clinician reported outcomes' },
  {
    key: STUDY_FEATURE_MAP.medicationAdherence,
    value: STUDY_FEATURE_MAP.medicationAdherence,
    text: 'Medication adherence',
  },
  { key: STUDY_FEATURE_MAP.superGems, value: STUDY_FEATURE_MAP.superGems, text: 'Treasure chest store' },
  { key: STUDY_FEATURE_MAP.testStudy, value: STUDY_FEATURE_MAP.testStudy, text: 'Test study' },
  { key: STUDY_FEATURE_MAP.emailless, value: STUDY_FEATURE_MAP.emailless, text: 'Emailless registration' },
  { key: STUDY_FEATURE_MAP.econsent, value: STUDY_FEATURE_MAP.econsent, text: 'eConsent' },
]

export const STUDY_SORTING_OPTIONS = {
  studyCreateDate: 'studyCreateDate',
  studyName: 'studyName',
}

export const STUDY_PAGINATION_SETTINGS_KEY = 'studyPaginationSettings'

export const DEFAULT_INSTRUMENT_ARCHITECTURE_VERSION = 1

export const MAX_PHOTO_LIMIT_FIELD_DEFAULT_VALUE = 3
export const MAX_PHOTO_LIMIT_MIN_VALUE = 3
export const MAX_PHOTO_LIMIT_MAX_VALUE = 25

export const getMaintenanceToolUrl = () => window.__APP_CONFIG__?.MAINTENANCE_TOOL_URL

export const STUDY_VERSION_ID_URL_PLACEHOLDER = 'live'

export const VERSION_STATUS_MAP = {
  live: 'live',
  dev: 'dev',
  uat: 'uat',
}

export const ROLE_OPTIONS = {
  ALL: 'all',
  II_III: 'ii_iii',
  III: 'iii',
}

/** For Study Promotion–enabled studies: all three Clinical Coordinator role IDs (Dev, UAT, Live), irrespective of study version. */
export const getClinicalCoordinatorRoleIdForStudyState = () => [
  USER_ROLE_ID_MAP.clinicalCoordinatorDEV,
  USER_ROLE_ID_MAP.clinicalCoordinatorUAT,
  USER_ROLE_ID_MAP.clinicalCoordinator,
]

/** For Study Promotion–enabled studies: Clinical Coordinator role label by study version status. */
export const getClinicalCoordinatorLabelForStudyState = versionStatus => {
  const status = (versionStatus || 'dev').toLowerCase()
  if (status === 'uat') return USER_ROLES_MAP.clinicalCoordinatorUAT
  if (status === 'live') return USER_ROLES_MAP.clinicalCoordinator
  return USER_ROLES_MAP.clinicalCoordinatorDEV
}

export const CLINICIAN_ROLES_OPTIONS = [
  {
    id: ROLE_OPTIONS.ALL,
    label: 'All clinicians (Level III, II & I)',
    value: CLINICIAN_ROLES_ID_LIST,
  },
  {
    id: ROLE_OPTIONS.II_III,
    label: 'Clinician Level III & II',
    value: [
      USER_ROLE_ID_MAP.clinRoII,
      USER_ROLE_ID_MAP.clinRoIII,
      USER_ROLE_ID_MAP.clinicalCoordinator,
      USER_ROLE_ID_MAP.impartialWitness,
    ],
  },
  {
    id: ROLE_OPTIONS.III,
    label: 'Clinician Level III only',
    value: [USER_ROLE_ID_MAP.clinRoIII, USER_ROLE_ID_MAP.clinicalCoordinator, USER_ROLE_ID_MAP.impartialWitness],
  },
]

export const PARTICIPANT_ID = 'Participant ID'
export const PRE_SCREEN_ID = 'Pre-Screen ID'
export const EMPTY_VALUE = '---'
export const SITE_NAME = 'Site name'
export const SUBJECT_ID = 'Subject ID'
export const DEFAULT_COMPLETION_METHOD = 'Participant App'
export const PARTICIPANT_KEYS = {
  SUBJECT_ID: 'subject_id',
  PRE_SCREEN_ID: 'pre_screen_id',
  DATE_OF_BIRTH: 'date_of_birth',
  START_DATE: 'start_date',
  HEIGHT: 'height',
  WEIGHT: 'weight',
  IS_CAREGIVER: 'is_caregiver',
  DEFAULT_LOCALE: 'default_locale',
  PHONE: 'phone',
}

export const PARTICIPANT_TABLE_ROW_KEY_MAP = {
  CHECKBOX: 'checkbox',
  RELATION_TO_PARTICIPANT: 'relationToParticipant',
  SITE_ID: 'siteId',
  ID: 'id',
}

// Reusable regex fragments for pattern validation
export const REGEX_PARTS = {
  upper: len => (len ? `[A-Z]{${len}}` : '[A-Z]+'),
  lower: len => (len ? `[a-z]{${len}}` : '[a-z]+'),
  mixed: len => (len ? `[A-Za-z]{${len}}` : '[A-Za-z]+'),
  digits: len => (len ? `\\d{${len}}` : '\\d+'),
  hexUpper: len => (len ? `[0-9A-F]{${len}}` : '[0-9A-F]+'),
  hexLower: len => (len ? `[0-9a-f]{${len}}` : '[0-9a-f]+'),
  hexMixed: len => (len ? `[0-9A-Fa-f]{${len}}` : '[0-9A-Fa-f]+'),
  alnum: len => (len ? `[A-Za-z0-9]{${len}}` : '[A-Za-z0-9]+'),
  alnumUpper: len => (len ? `[A-Za-z0-9-]{${len}}` : '[A-Za-z0-9-]+'),
  alnumUpperWithDigit: len => (len ? `(?=[A-Z0-9]*\\d)[A-Z0-9]{${len}}` : '(?=[A-Z0-9]*\\d)[A-Z0-9]+'),
  alnumLowerWithDigit: len => (len ? `(?=[a-z0-9]*\\d)[a-z0-9]{${len}}` : '(?=[a-z0-9]*\\d)[a-z0-9]+'),
  alnumMixedWithDigit: len => (len ? `(?=[A-Za-z0-9]*\\d)[A-Za-z0-9]{${len}}` : '(?=[A-Za-z0-9]*\\d)[A-Za-z0-9]+'),
}

// Character class strings for sanitization (non-regex, used in replace operations)
export const CHAR_CLASSES = {
  LETTERS: 'a-zA-Z',
  DIGITS: '0-9',
  HEX_UPPER: '0-9A-F',
  HEX_LOWER: '0-9a-f',
  HEX_MIXED: '0-9A-Fa-f',
  ALNUM: 'a-zA-Z0-9',
  ALNUM_WITH_SEPARATORS: 'a-zA-Z0-9\\-_',
}

// Placeholder sample strings for pattern input fields
export const PLACEHOLDER_SAMPLES = {
  LETTERS_UPPER: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  LETTERS_LOWER: 'abcdefghijklmnopqrstuvwxyz',
  DIGITS: '1234567890',
  HEX_UPPER: 'ABCDEF1234567890',
  HEX_LOWER: 'abcdef1234567890',
  ALNUM_UPPER_PATTERN: 'A1B2C3',
  ALNUM_LOWER_START: 'a',
  ALNUM_MIXED_START: 'A',
  GENERIC_LETTER: 'XXXX',
  GENERIC_DIGIT: '9999',
  GENERIC_ALNUM: 'X9X9',
  GENERIC_CHAR: 'X',
  INCR_DEFAULT: '01',
}

// Pattern matching keywords used for generic rule inference
export const PATTERN_KEYWORDS = {
  CHAR: 'CHAR',
  NUM: 'NUM',
  ALPHANUMERIC: 'ALPHANUMERIC',
}

// Regex flags
export const REGEX_FLAGS = {
  GLOBAL: 'g',
}

// Common token names used for ID format patterns (subject ID / pre-screen ID)
export const ID_PATTERN_TOKENS = {
  LETTER: 'LETTER',
  LETTERS: 'LETTERS',
  LETTERS_UPPERCASE: 'LETTERS_UPPERCASE',
  LETTERS_LOWERCASE: 'LETTERS_LOWERCASE',
  LETTERS_MIXED: 'LETTERS_MIXED',
  LETTER_LOWER: 'LETTER_LOWER',
  LETTER_MIXED: 'LETTER_MIXED',
  DIGITS: 'DIGITS',
  DIGIT: 'DIGIT',
  INCR: 'INCR',
  INCREMENT: 'INCREMENT',
  INCREMENTAL: 'INCREMENTAL',
  STUDY_ID: 'STUDY_ID',
  SITE_ID: 'SITE_ID',
  ALNUM: 'ALNUM',
  ALNUM_LOWER: 'ALNUM_LOWER',
  ALNUM_MIXED: 'ALNUM_MIXED',
  ALNUM_UPPERCASE: 'ALNUM_UPPERCASE',
  ALNUM_LOWERCASE: 'ALNUM_LOWERCASE',
  ALPHANUMERIC_UPPERCASE: 'ALPHANUMERIC_UPPERCASE',
  ALPHANUMERIC_LOWERCASE: 'ALPHANUMERIC_LOWERCASE',
  ALPHANUMERIC_MIXED: 'ALPHANUMERIC_MIXED',
  HEX_UPPERCASE: 'HEX_UPPERCASE',
  HEX_LOWERCASE: 'HEX_LOWERCASE',
  HEX: 'HEX',
  LITERAL: 'LITERAL',
}

export const FORM_FIELDS = [
  {
    key: 'default_locale',
    text: 'Locale',
    type: 'select',
    // options for the dropdown will be populated by the either the '/locales' API endpoint or list of enforced languages if available,
    // not hard coded here to allow for more locales as they come
  },
  {
    key: 'fname',
    text: 'Preferred Name',
    type: 'text',
    placeholder: 'Preferred Name',
    tester: true,
  },
  {
    key: 'lname',
    text: 'Last Name',
    type: 'text',
    placeholder: 'Last Name',
    tester: true,
  },
  {
    key: 'pre_screen_id',
    text: 'Pre-Screen ID',
    type: 'text',
    placeholder: 'Pre-Screen ID',
    isAutoGenerate: 'is_auto_generated',
    autoGenerateFormat: 'auto_gen_format',
    tester: true,
  },
  {
    key: 'subject_id',
    text: 'Subject ID',
    type: 'text',
    placeholder: 'Subject ID',
    isAutoGenerate: 'is_auto_generated',
    autoGenerateFormat: 'auto_gen_format',
    tester: true,
  },
  { key: 'is_caregiver', text: 'Caregiver', type: 'check' },
  {
    key: 'gender',
    text: 'Sex',
    type: 'select',
    options: [
      { key: 'm', text: 'Male', value: 'm' },
      { key: 'f', text: 'Female', value: 'f' },
      { key: 'o', text: 'Other', value: 'o' },
      { key: 'n', text: '---', value: 'n' },
    ],
  },
  {
    key: 'height',
    text: 'Height',
    type: 'number',
    options: [
      { key: 'cm', text: 'cm', value: 'cm', placeholder: '200' },
      { key: 'feet/inches', text: 'feet/inches', value: 'feet/inches', placeholder: '5 8' },
    ],
  },
  {
    key: 'weight',
    text: 'Weight',
    placeholder: '100',
    type: 'number',
    options: [
      { key: 'kg', text: 'kg', value: 'kg' },
      { key: 'pounds', text: 'pounds', value: 'pounds' },
    ],
  },
  {
    key: 'ethnicity',
    text: 'Ethnicity',
    type: 'select',
  },
  {
    key: 'date_of_birth',
    text: 'Date of Birth',
    type: 'date',
    tester: true,
  },
  {
    key: 'start_date',
    text: 'Start Date',
    type: 'date',
  },
  {
    key: 'email',
    text: 'Email Address',
    type: 'text',
    placeholder: 'email@email.com',
    tester: true,
  },
  {
    key: 'phone',
    text: 'Phone Number',
    placeholder: 'Phone Number',
    // type: 'text',
    type: 'phone',
  },
]

export const FORM_FIELDS_CARE_GIVER = [
  {
    key: 'fname',
    text: 'Preferred Name',
    type: 'text',
    placeholder: 'Preferred Name',
    tester: true,
  },
  {
    key: 'lname',
    text: 'Last Name',
    type: 'text',
    placeholder: 'Last Name',
    tester: true,
  },
  {
    key: 'subject_id',
    text: 'Subject ID',
    type: 'text',
    placeholder: 'Subject ID',
    tester: true,
  },
  {
    key: 'default_locale',
    text: 'Locale',
    type: 'select',
  },
]

export function isAtLeastVersion(currentVersion = '0.0.0', targetVersion = '0.0.0') {
  const parse = v => (v || '0.0.0').split('.').map(n => parseInt(n, 10) || 0)
  const [a1, a2, a3] = parse(currentVersion)
  const [b1, b2, b3] = parse(targetVersion)

  if (a1 !== b1) return a1 > b1
  if (a2 !== b2) return a2 > b2
  return a3 >= b3
}

export const PRE_SCREEN_ID_PLACEHOLDER = '123-XYZ-001 '

export const FROALA_KEY = 'bMA6aD6A4D3B2B1B2F1yQNDMIJg1IQNSEa1EUAi1XVFQd1EaG3C2A5C3C4D3C2D4F2G1=='
